<script setup>
/* eslint-disable no-alert */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useStore } from 'dashboard/composables/store';
import { DuplicateContactException } from 'shared/helpers/CustomErrors';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import {
  ContractTemplatesAPI,
  ContractsAPI,
} from 'dashboard/api/talkviaContracts';
import TalkviaCreditProductsAPI from 'dashboard/api/talkviaCreditProducts';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import TalkviaInventoryProductGroupsAPI from 'dashboard/api/talkviaInventoryProductGroups';
import ContactsAPI from 'dashboard/api/contacts';
import QuotesAPI from 'dashboard/api/talkviaQuotes';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useDraggablePanel } from 'dashboard/components-next/dialog/useDraggablePanel';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { usePaymentMethods } from 'dashboard/composables/usePaymentMethods';
import { contractScheduleError, contractStepError } from './contractWizard';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { financialLabel } from './financePresentation';
import ContractSendDialog from './ContractSendDialog.vue';
import SignatureLevelPicker from './SignatureLevelPicker.vue';
import ContractStockPending from './ContractStockPending.vue';
import ContractCreditsDialog from './ContractCreditsDialog.vue';
import QuotesPanel from './QuotesPanel.vue';
import DeliveryMessageSection from './DeliveryMessageSection.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import {
  buildCustomQuoteDocument,
  buildDefaultQuoteDocument,
  quoteDocumentTitle,
  renderQuoteTemplateBody,
} from './quoteDocument';
import { renderQuoteAttachment } from './quoteAttachment';
import { quoteTemplateValues } from './quoteTemplateValues';
import {
  contractQuoteInvoiceItems,
  quoteContractItems,
  quoteInvoiceItems,
} from './quoteInvoiceItems';
import DateField from 'dashboard/components-next/date-field/DateField.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import {
  normalizeRentalDays,
  rentalItemTotal,
  itemRentalDays,
  skipsRentalDays,
  suggestRentalDays,
} from './rentalDays';

// `generatorOnly`: monta só o assistente "Gerar contrato" (e os diálogos de envio) para
// abri-lo fora da página, como na Central do cliente da conversa. A página segue igual.
const props = defineProps({
  generatorOnly: { type: Boolean, default: false },
});
const emit = defineEmits(['created']);

const copy = {
  bodyFormatLabel: 'Formato do conteúdo',
  bodyFormatText: 'Editor de texto',
  bodyFormatHtml: 'HTML (layout próprio)',
  bodyFormatHtmlHint:
    'Cole o HTML completo, com estilos e imagens embutidas. Scripts, formulários e quadros são removidos. O cliente vê o layout na assinatura; o comprovante em PDF traz o texto do contrato.',
  htmlAsCodeWarning:
    'Este modelo tem HTML colado no editor de texto: o cliente veria as linhas de código. Converta para o formato HTML para usar o layout.',
  convertToHtml: 'Converter para HTML',
  linkMessageButton: 'Mensagem do link de assinatura',
  linkMessageTitle: 'Mensagem do link de assinatura',
  linkMessageDescription:
    'Texto enviado na conversa junto com o link do contrato. Fora da janela de 24 h do WhatsApp, vale o modelo aprovado escolhido no envio.',
  linkMessageVariables: 'Parâmetros',
  linkMessagePreview: 'Como o cliente recebe',
  linkMessageSave: 'Salvar mensagem',
  linkMessageRestore: 'Restaurar padrão',
  linkMessageSaved: 'Mensagem do link salva.',
  linkMessageError: 'Não foi possível salvar a mensagem. Tente novamente.',
  linkMessageLoadError: 'Não foi possível carregar a mensagem atual.',
  previewTemplate: 'Pré-visualizar',
  previewTitle: 'Prévia do modelo',
  previewDescription:
    'Como o cliente vê o documento, com dados fictícios (Tom Martins, 2 diárias, mesa e frete). Nada é salvo.',
  previewLoading: 'Montando a prévia…',
  previewError:
    'Não foi possível montar a prévia agora. Tente novamente em instantes.',
  previewWarnings: {
    html_omitted:
      'Trechos em HTML não aparecem para o cliente: a assinatura usa a formatação do editor (títulos, negrito, listas). Isso inclui {{produtos_tabela}}; os produtos aparecem no quadro próprio da página de assinatura.',
    html_as_code:
      'O modelo tem HTML colado como código: o cliente verá essas linhas como código. Para um layout próprio, use a formatação do editor.',
  },
  chargePerDay: 'Cobrar por diária',
  title: 'Contratos e orçamentos',
  description:
    'Gerencie contratos, acompanhe assinaturas e organize os orçamentos dos clientes.',
  templates: 'Modelos de contrato',
  contracts: 'Contratos gerados',
  newTemplate: 'Novo modelo',
  saveTemplate: 'Salvar modelo',
  updateTemplate: 'Atualizar modelo',
  generateContract: 'Gerar contrato',
  addProduct: 'Adicionar produto',
  sendLink: 'Enviar link',
  sendToContact: 'Enviar contrato para contato',
  sendQuote: 'Enviar orçamento',
  openConversation: 'Abrir conversa',
  openLink: 'Abrir',
  delete: 'Excluir',
  deleteTitle: 'Excluir contrato',
  deleteDescription:
    'Confirme para excluir este contrato. Esta ação não poderá ser desfeita.',
  cancel: 'Cancelar',
  confirmDelete: 'Excluir contrato',
  statusUpdated: 'Status do contrato atualizado.',
  pendingReopened:
    'Contrato reaberto para assinatura. Um novo link foi gerado com validade de 3 dias; a assinatura anterior ficou arquivada.',
  reopenTitle: 'Reabrir contrato para nova assinatura',
  reopenDescription:
    'O link atual deixa de funcionar e um novo será gerado. A assinatura anterior e seus anexos ficam arquivados como prova e continuam na trilha do contrato.',
  reopenConfirm: 'Reabrir contrato',
  reopenReasonLabel: 'Motivo da reabertura',
  reopenReasonPlaceholder:
    'Ex.: cliente pediu correção do valor antes de assinar novamente',
  pending: 'Pendentes',
  signed: 'Assinados',
  expired: 'Expirados',
  cancelled: 'Cancelados',
  awaiting_delivery: 'Aguardando entrega',
  completed: 'Concluído',
  all: 'Todos',
  searchPlaceholder: 'Buscar por telefone, CPF/CNPJ ou nome',
  bodyHint: 'Clique em um parâmetro para inserir no editor.',
  downloadReceipt: 'Baixar comprovante PDF',
  viewDocuments: 'Ver anexos',
  documentsTitle: 'Anexos do contrato',
  noDocuments: 'Nenhum anexo encontrado.',
  downloadDocument: 'Baixar',
  requiredDocuments: 'Documentos para assinatura',
  customDocument: 'Outro documento',
  invoiceTotal: 'Total final',
  contactSearch: 'Contato cadastrado',
  edit: 'Editar',
  close: 'Fechar',
  manageTemplates: 'Modelos',
  contractTemplates: 'Contratos',
  quoteTemplates: 'Orçamentos',
  newQuoteTemplate: 'Novo orçamento',
  addPaymentMethod: 'Adicionar forma de pagamento',
  paymentMethodSaved: 'Forma de pagamento salva.',
  dashboard: 'Dashboard',
  editContract: 'Editar contrato',
  editContractTitle: 'Editar contrato',
  saveContract: 'Salvar contrato',
  contractUpdated: 'Contrato atualizado.',
  contractUpdateFailed: 'Não foi possível atualizar o contrato.',
  contractEditLocked:
    'Contratos assinados ou concluídos não podem ser editados.',
  deliveryDate: 'Data de entrega',
  pickupDate: 'Data de retirada',
  deliveryRange: 'Entrega / retirada',
  addressSection: 'Endereço do contato',
  fullAddress: 'Endereço completo',
  addressMergeHint: 'Use {{endereco_completo}} para mesclar tudo no modelo.',
  useContactAddress: 'Usar endereço cadastrado no contato',
  customContractAddress: 'Usar outro endereço neste contrato',
  lookupZipCode: 'Consultar CEP',
  zipCodeNotFound: 'CEP não encontrado.',
};

const CUSTOM_SERVICE_PRODUCT_TYPE = 'TalkviaCustomService';
const CUSTOM_SERVICE_VALUE = 'custom-service';

const PARAMETER_OPTIONS = [
  '{{nome}}',
  '{{cpf}}',
  '{{cnpj}}',
  '{{documento}}',
  '{{tipo_documento}}',
  '{{telefone}}',
  '{{data_geracao}}',
  '{{data_assinatura}}',
  '{{data_entrega}}',
  '{{data_retirada}}',
  '{{cep}}',
  '{{endereco}}',
  '{{numero}}',
  '{{numero_endereco}}',
  '{{numero_orcamento}}',
  '{{complemento}}',
  '{{bairro}}',
  '{{cidade}}',
  '{{uf}}',
  '{{endereco_completo}}',
  '{{produto}}',
  '{{produtos}}',
  '{{produtos_lista}}',
  '{{produtos_tabela}}',
  '{{total}}',
  '{{total_produtos}}',
  '{{valor_total_produtos}}',
  '{{forma_pagamento}}',
];

// Parâmetros do modelo agrupados por assunto no editor; todo parâmetro de
// PARAMETER_OPTIONS aparece em exatamente um grupo.
const PARAMETER_GROUP_DEFINITIONS = [
  {
    key: 'client',
    label: 'Cliente',
    icon: 'i-hugeicons-user',
    items: ['nome', 'cpf', 'cnpj', 'documento', 'tipo_documento', 'telefone'],
  },
  {
    key: 'address',
    label: 'Endereço',
    icon: 'i-hugeicons-location-01',
    items: [
      'cep',
      'endereco',
      'numero',
      'numero_endereco',
      'complemento',
      'bairro',
      'cidade',
      'uf',
      'endereco_completo',
    ],
  },
  {
    key: 'dates',
    label: 'Datas',
    icon: 'i-hugeicons-calendar-03',
    items: ['data_geracao', 'data_assinatura', 'data_entrega', 'data_retirada'],
  },
];
const PARAMETER_GROUPS = [
  ...PARAMETER_GROUP_DEFINITIONS.map(group => ({
    ...group,
    items: group.items.map(item => `{{${item}}}`),
  })),
  {
    key: 'values',
    label: 'Produtos e valores',
    icon: 'i-hugeicons-money-bag-02',
    items: PARAMETER_OPTIONS.filter(
      parameter =>
        !PARAMETER_GROUP_DEFINITIONS.some(group =>
          group.items.includes(parameter.slice(2, -2))
        )
    ),
  },
  {
    key: 'signature',
    label: 'Assinatura',
    icon: 'i-hugeicons-link-01',
    items: ['{{link_assinatura}}'],
    quoteOnly: true,
  },
];

const DEFAULT_DOCUMENTS = ['CPF/CNH', 'Comprovante Endereço', 'RG'];
const FIELD_LABEL_CLASS =
  'mb-0 grid gap-1.5 text-sm font-medium text-n-slate-12';
const FIELD_CONTROL_CLASS =
  '!mb-0 h-10 w-full rounded-lg !border !border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 shadow-sm transition placeholder:text-n-slate-10 hover:bg-n-alpha-2 focus:!border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand disabled:cursor-not-allowed disabled:opacity-50';
const INPUT_CLASS = `reset-base ${FIELD_CONTROL_CLASS}`;
const EDIT_FIELD_LABEL_CLASS =
  'mb-0 grid gap-1 text-xs font-semibold text-n-slate-11';
const EDIT_INPUT_CLASS = `reset-base ${FIELD_CONTROL_CLASS} !h-9 !rounded-md !px-2.5 !text-sm`;
const statusOptions = [
  { label: copy.all, value: '' },
  { label: copy.pending, value: 'pending' },
  { label: copy.signed, value: 'signed' },
  { label: copy.awaiting_delivery, value: 'awaiting_delivery' },
  { label: copy.completed, value: 'completed' },
  { label: copy.expired, value: 'expired' },
  { label: copy.cancelled, value: 'cancelled' },
];
const contractStatusOptions = statusOptions.filter(option => option.value);
const dateRangeFilters = [
  {
    key: 'delivery',
    label: 'Entrega',
    startKey: 'start_at',
    endKey: 'end_at',
  },
  {
    key: 'pickup',
    label: 'Retirada',
    startKey: 'pickup_start_at',
    endKey: 'pickup_end_at',
  },
];
const dateRangePresets = [
  { key: 'last7', label: 'Últimos 7 dias' },
  { key: 'last30', label: 'Últimos 30 dias' },
  { key: 'last3m', label: 'Últimos 3 meses' },
  { key: 'last6m', label: 'Últimos 6 meses' },
  { key: 'lastYear', label: 'Ano passado' },
  { key: 'thisWeek', label: 'Esta semana' },
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'custom', label: 'Intervalo personalizado' },
];
const weekDays = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

const { currentAccount, updateAccount } = useAccount();
const { isAdmin } = useAdmin();
const store = useStore();
const createContactDialogRef = ref(null);
const isCreatingContact = ref(false);

const templates = ref([]);
const contracts = ref([]);
const allContracts = ref([]);
const summary = ref({});
const creditProducts = ref([]);
const inventoryProducts = ref([]);
const inventoryProductGroups = ref([]);
const isLoading = ref(false);
const selectedTemplateId = ref('');
const filters = reactive({
  status: '',
  q: '',
  start_at: '',
  end_at: '',
  pickup_start_at: '',
  pickup_end_at: '',
});
const activeDateRangeFilter = ref('');
const dateRangeFilterRefs = ref({});
const formSelectMenuRefs = ref({});
const dateRangeDraft = reactive({
  start: '',
  end: '',
  preset: 'custom',
  month: '',
});
const contactSearch = ref('');
const contactResults = ref([]);
const isSearchingContacts = ref(false);
const contractSendDialogRef = ref(null);
const deleteContractDialogRef = ref(null);
const showDocumentsModal = ref(false);
const selectedDocuments = ref([]);
const selectedDocumentsName = ref('');
const contractPendingDelete = ref(null);
const isDeletingContract = ref(false);
const updatingStatusContractId = ref(null);
const openContractStatusMenuId = ref(null);
const openContractActionsId = ref(null);
const showContractStatusFilter = ref(false);
const openFormSelectMenu = ref('');
const router = useRouter();
const route = useRoute();
const documentTabs = [
  { label: 'Contratos', value: 'contracts' },
  { label: 'Orçamentos', value: 'quotes' },
];
const activeDocumentTab = computed(() =>
  route.query.section === 'quotes' || route.query.edit_quote_id ? 1 : 0
);
const changeDocumentTab = tab => {
  const query = { ...route.query, section: tab.value };
  delete query.edit_quote_id;
  router.replace({ query });
};

// Stock by date
const stockDate = ref(new Date().toISOString().slice(0, 10));
const stockDateMonth = ref(stockDate.value);
const showStockDatePicker = ref(false);
const stockDatePickerRef = ref(null);
const stockByDateResults = ref([]);
const isLoadingStock = ref(false);

const { t: translate } = useI18n();
const showGenerateContractModal = ref(false);
// Aberto pela conversa, o assistente flutua sem fundo escurecido e pode ser arrastado,
// para o atendente ler a conversa enquanto monta o contrato.
const generatorPanelRef = ref(null);
const {
  panelStyle: generatorPanelStyle,
  startDrag: startGeneratorDrag,
  onHandleKeydown: onGeneratorHandleKeydown,
  reset: resetGeneratorPosition,
} = useDraggablePanel(generatorPanelRef);
const contractStep = ref(0);
const contractStepMessage = ref('');
const isGeneratingContract = ref(false);
const contractStepBody = ref(null);
const contractSteps = [
  {
    title: 'Cliente',
    description: 'Quem vai contratar',
    icon: 'i-lucide-user-round',
  },
  {
    title: 'Produtos e serviços',
    description: 'Monte os itens do contrato',
    icon: 'i-lucide-package',
  },
  {
    title: 'Entrega e pagamento',
    description: 'Datas, pagamento e assinatura',
    icon: 'i-lucide-truck',
  },
  {
    title: 'Revisão',
    description: 'Confira antes de gerar',
    icon: 'i-lucide-file-check-2',
  },
];

// Template modal
const showTemplateModal = ref(false);
const templateModalKind = ref('contract');
const templatePreviewDialog = ref(null);
const linkMessageDialog = ref(null);
const linkMessage = reactive({
  text: '',
  defaultText: '',
  loading: false,
  saving: false,
});
const LINK_MESSAGE_VARIABLES = ['{{nome}}', '{{empresa}}', '{{link}}'];
const linkMessagePreview = computed(() =>
  String(linkMessage.text || linkMessage.defaultText)
    .replace(/\{\{\s*nome\s*\}\}/g, 'Tom Martins')
    .replace(
      /\{\{\s*empresa\s*\}\}/g,
      currentAccount.value?.name || 'Sua empresa'
    )
    .replace(
      /\{\{\s*link\s*\}\}/g,
      'https://link.talkvia.com.br/talkvia/contracts/exemplo'
    )
);

const openLinkMessage = async () => {
  linkMessageDialog.value?.open();
  linkMessage.loading = true;
  try {
    const { data } = await ContractTemplatesAPI.getLinkMessage();
    linkMessage.text = data?.message || '';
    linkMessage.defaultText = data?.default || '';
  } catch {
    useAlert(copy.linkMessageLoadError);
  } finally {
    linkMessage.loading = false;
  }
};

const insertLinkMessageVariable = variable => {
  const field = document.querySelector('[data-link-message] textarea');
  const text = linkMessage.text || '';
  if (!field) {
    linkMessage.text = `${text}${variable}`;
    return;
  }
  const start = field.selectionStart ?? text.length;
  const end = field.selectionEnd ?? text.length;
  linkMessage.text = `${text.slice(0, start)}${variable}${text.slice(end)}`;
  requestAnimationFrame(() => {
    field.focus();
    field.setSelectionRange(start + variable.length, start + variable.length);
  });
};

const saveLinkMessage = async () => {
  if (linkMessage.saving) return;
  linkMessage.saving = true;
  try {
    const { data } = await ContractTemplatesAPI.updateLinkMessage(
      linkMessage.text === linkMessage.defaultText ? '' : linkMessage.text
    );
    linkMessage.text = data?.message || linkMessage.text;
    linkMessageDialog.value?.close();
    useAlert(copy.linkMessageSaved);
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.linkMessageError);
  } finally {
    linkMessage.saving = false;
  }
};
const templatePreview = reactive({
  loading: false,
  html: '',
  warnings: [],
  error: '',
});
let templatePreviewRequestId = 0;

// Dashboard modal
const showDashboard = ref(false);
const creditsDialogRef = ref(null);
const isDashboardExpanded = ref(false);

const useContactAddress = ref(true);
const selectedContactAddress = ref({});
const selectedContact = ref(null);
const selectedDocumentType = ref('');
const isAddressSectionCollapsed = ref(false);
const isFetchingContractZipCode = ref(false);
const lastContractZipCodeLookup = ref('');
const isFetchingContractEditZipCode = ref(false);
const lastContractEditZipCodeLookup = ref('');

// Payment method management
const customPaymentMethods = ref([]);
const showAddPaymentInput = ref(false);
const newPaymentMethodInput = ref('');
const isSavingPaymentMethod = ref(false);

const { allPaymentMethods, paymentMethodOptions } = usePaymentMethods();

const templateForm = reactive({
  id: null,
  name: '',
  body: '',
  active: true,
  require_selfie: true,
  metadata: {
    required_documents: [...DEFAULT_DOCUMENTS],
  },
});

const customDocumentName = ref('');

const contractForm = reactive({
  talkvia_contract_template_id: '',
  contact_id: '',
  recipient_name: '',
  recipient_cpf: '',
  recipient_phone: '',
  recipient_zip_code: '',
  recipient_address: '',
  recipient_address_number: '',
  recipient_address_complement: '',
  recipient_neighborhood: '',
  recipient_city: '',
  recipient_state: '',
  recipient_address_source: 'contact',
  scheduled_at: '',
  pickup_at: '',
  payment_method: '',
  signature_level: 'simple',
  rental_days: '1',
});

// Níveis de assinatura: a disponibilidade vem do servidor (configuração e créditos da empresa).
const signatureLevels = ref({});
// Franquia mensal de contratos do plano e saldo de contratos avulsos.
const contractAllowance = ref(null);
const contractAllowanceLabel = computed(() => {
  const allowance = contractAllowance.value;
  if (!allowance) return '';
  const plan = allowance.plan_name
    ? `Plano ${allowance.plan_name}`
    : 'Sem plano';
  const franchise = allowance.unlimited
    ? 'contratos ilimitados no ciclo'
    : `${allowance.used_from_plan} de ${allowance.monthly_limit} contratos da franquia usados no ciclo`;
  return `${plan}: ${franchise} · ${allowance.credit_balance} avulso(s) disponível(is)`;
});
const SIGNATURE_LEVEL_PRESENTATION = [
  {
    value: 'simple',
    label: 'Simples',
    icon: 'i-hugeicons-signature',
    description:
      'Aceite na página, com registro de data, IP, navegador e integridade.',
  },
  {
    value: 'reinforced',
    label: 'Reforçada',
    icon: 'i-hugeicons-security-check',
    description:
      'Código no WhatsApp do cliente e conferência de nome, CPF e nascimento (Talkvia Cadastro).',
  },
  {
    value: 'biometric',
    label: 'Biométrica Serpro',
    icon: 'i-hugeicons-face-id',
    description:
      'Código no WhatsApp e validação de dados e rosto, com prova de vida, nas bases oficiais do governo. Usa 1 biometria da franquia do plano ou dos créditos comprados.',
  },
];
const signatureLevelOptions = computed(() =>
  SIGNATURE_LEVEL_PRESENTATION.map(option => {
    const status = signatureLevels.value[option.value] || {};
    return {
      ...option,
      available: option.value === 'simple' || status.available === true,
      reason: status.reason || '',
      balance: status.balance,
    };
  })
);
const loadSignatureLevels = async () => {
  try {
    const { data } = await ContractsAPI.signatureLevels();
    signatureLevels.value = data.levels || {};
    contractAllowance.value = data.allowance || null;
  } catch {
    signatureLevels.value = {};
    contractAllowance.value = null;
  }
  const selected = signatureLevelOptions.value.find(
    option => option.value === contractForm.signature_level
  );
  if (!selected?.available) contractForm.signature_level = 'simple';
};
const selectedSignatureLevel = computed(
  () =>
    signatureLevelOptions.value.find(
      option => option.value === contractForm.signature_level
    ) || SIGNATURE_LEVEL_PRESENTATION[0]
);
// Saldos que o nível escolhido pode consumir: franquia de contratos, avulsos,
// consultas do Talkvia Cadastro (reforçada) e validações Serpro (biométrica).
const creditSummary = computed(() => {
  const allowance = contractAllowance.value;
  const levels = signatureLevels.value;
  const items = [];
  if (allowance) {
    items.push({
      key: 'franchise',
      label: 'Franquia de contratos no ciclo',
      value: allowance.unlimited
        ? 'Ilimitada'
        : `${allowance.remaining_from_plan} de ${allowance.monthly_limit}`,
      hint: allowance.plan_name ? `Plano ${allowance.plan_name}` : 'Sem plano',
    });
    items.push({
      key: 'contracts',
      label: 'Contratos avulsos',
      value: String(allowance.credit_balance),
      hint: 'Usados depois da franquia',
    });
  }
  if (levels.reinforced?.balance !== undefined)
    items.push({
      key: 'confirme',
      label: 'Conferências de dados',
      value: String(levels.reinforced.balance),
      hint: 'Assinatura reforçada',
    });
  if (levels.biometric?.balance !== undefined)
    items.push({
      key: 'serpro',
      label: 'Biometrias disponíveis',
      value: String(levels.biometric.balance),
      hint: 'Franquia do ciclo + créditos comprados',
    });
  return items;
});

const showContractEditModal = ref(false);
const editingContract = ref(null);
const isSavingContractEdit = ref(false);
const contractEditForm = reactive({
  recipient_name: '',
  recipient_cpf: '',
  recipient_phone: '',
  recipient_zip_code: '',
  recipient_address: '',
  recipient_address_number: '',
  recipient_address_complement: '',
  recipient_neighborhood: '',
  recipient_city: '',
  recipient_state: '',
  recipient_address_source: 'contract',
  scheduled_at: '',
  pickup_at: '',
  payment_method: '',
  talkvia_contract_template_id: '',
  signature_level: 'simple',
  rental_days: '1',
});

// Na edição, o nível atual do contrato continua selecionável mesmo que hoje esteja
// indisponível: o servidor só revalida a disponibilidade quando o nível muda.
const contractEditSignatureLevelOptions = computed(() =>
  signatureLevelOptions.value.map(option => {
    const current = editingContract.value?.signature_level === option.value;
    return { ...option, current, available: option.available || current };
  })
);

const invoiceItemForm = reactive({
  product_value: '',
  custom_name: '',
  quantity: 1,
  unit_price: '',
});

const invoiceItems = ref([]);

const contractEditInvoiceItemForm = reactive({
  product_value: '',
  custom_name: '',
  quantity: 1,
  unit_price: '',
});

const contractEditInvoiceItems = ref([]);

const productPrice = (...values) => {
  const rawValue = values.find(
    value => value !== undefined && value !== null && value !== ''
  );
  if (rawValue === undefined) return 0;
  if (typeof rawValue === 'number') return rawValue;

  const normalizedValue = String(rawValue).trim();
  if (normalizedValue.includes(',')) {
    return (
      Number(
        normalizedValue
          .replace(/\./g, '')
          .replace(',', '.')
          .replace(/[^\d.-]/g, '')
      ) || 0
    );
  }

  return Number(normalizedValue.replace(/[^\d.-]/g, '')) || 0;
};

const productOptionsFor = form => [
  {
    label: 'Serviço personalizado',
    value: CUSTOM_SERVICE_VALUE,
    icon: 'i-hugeicons-wrench-01',
    productType: CUSTOM_SERVICE_PRODUCT_TYPE,
    productId: null,
    name: form.custom_name || 'Serviço personalizado',
    price: 0,
  },
  ...creditProducts.value.map(product => ({
    label: `Crédito · ${product.name}`,
    value: `TalkviaCreditProduct:${product.id}`,
    icon: 'i-hugeicons-credit-card',
    productType: 'TalkviaCreditProduct',
    productId: product.id,
    name: product.name,
    price: productPrice(product.sale_price, product.value),
  })),
  ...inventoryProducts.value.map(product => ({
    label: `Estoque · ${product.name}`,
    value: `TalkviaInventoryProduct:${product.id}`,
    icon: 'i-hugeicons-package',
    productType: 'TalkviaInventoryProduct',
    productId: product.id,
    name: product.name,
    price: productPrice(product.sale_price),
  })),
  ...inventoryProductGroups.value.map(group => ({
    label: `Grupo · ${group.name}`,
    value: `TalkviaInventoryProductGroup:${group.id}`,
    icon: 'i-hugeicons-boxes',
    productType: 'TalkviaInventoryProductGroup',
    productId: group.id,
    name: group.name,
    price: productPrice(group.sale_price),
  })),
];

const productOptions = computed(() => productOptionsFor(invoiceItemForm));
const contractEditProductOptions = computed(() =>
  productOptionsFor(contractEditInvoiceItemForm)
);

const templateKind = template => template.metadata?.kind || 'contract';

const contractTemplates = computed(() =>
  templates.value.filter(template => templateKind(template) === 'contract')
);

const quoteTemplates = computed(() =>
  templates.value.filter(template => templateKind(template) === 'quote')
);

const visibleTemplates = computed(() =>
  templates.value.filter(
    template => templateKind(template) === templateModalKind.value
  )
);

const activeTemplateOptions = computed(() =>
  contractTemplates.value
    .filter(item => item.active)
    .map(template => ({ label: template.name, value: template.id }))
);

const generateTemplateLabel = computed(
  () =>
    activeTemplateOptions.value.find(
      option =>
        Number(option.value) ===
        Number(contractForm.talkvia_contract_template_id)
    )?.label || 'Selecione o modelo'
);

// O modelo atual continua na lista mesmo se foi desativado depois da criação do contrato.
const contractEditTemplateOptions = computed(() => {
  const options = [...activeTemplateOptions.value];
  const current = editingContract.value;
  if (
    current?.talkvia_contract_template_id &&
    !options.some(
      option =>
        Number(option.value) === Number(current.talkvia_contract_template_id)
    )
  ) {
    options.push({
      value: current.talkvia_contract_template_id,
      label: `${current.template_name || 'Modelo atual'} (inativo)`,
    });
  }
  return options;
});

const contractEditRequiredDocuments = computed(() => {
  const template = contractTemplates.value.find(
    item =>
      Number(item.id) === Number(contractEditForm.talkvia_contract_template_id)
  );
  return template?.metadata?.required_documents || [];
});

// Trocar modelo ou nível muda o que o cliente assina: avisar antes de salvar.
const contractEditChangesSignature = computed(() => {
  const current = editingContract.value;
  if (!current) return false;
  return (
    Number(contractEditForm.talkvia_contract_template_id) !==
      Number(current.talkvia_contract_template_id) ||
    contractEditForm.signature_level !== current.signature_level
  );
});

const editPaymentMethodOptions = computed(() => {
  const methods = [...paymentMethodOptions.value];
  const current = contractEditForm.payment_method;
  if (current && !methods.some(method => method.value === current)) {
    methods.push({ value: current, label: current });
  }
  return [{ value: '', label: 'Não informado' }, ...methods];
});

const isCustomServiceItem = computed(
  () => invoiceItemForm.product_value === CUSTOM_SERVICE_VALUE
);

const isContractEditCustomServiceItem = computed(
  () => contractEditInvoiceItemForm.product_value === CUSTOM_SERVICE_VALUE
);

const canAddInvoiceItem = computed(() => {
  if (!invoiceItemForm.product_value) return false;
  if (isCustomServiceItem.value && !invoiceItemForm.custom_name.trim()) {
    return false;
  }
  return true;
});

const canAddContractEditInvoiceItem = computed(() => {
  if (!contractEditInvoiceItemForm.product_value) return false;
  if (
    isContractEditCustomServiceItem.value &&
    !contractEditInvoiceItemForm.custom_name.trim()
  ) {
    return false;
  }
  return true;
});

const closeFormSelectMenu = () => {
  openFormSelectMenu.value = '';
};

const quoteTemplateBody = () =>
  [
    '<p><strong>PROPOSTA DE LOCAÇÃO Nº {{numero_orcamento}}</strong></p>',
    '<p>{{data_geracao}}</p>',
    '<p><strong>Identificação:</strong> {{nome}}</p>',
    '<p><strong>Para:</strong> {{nome}}<br><strong>{{tipo_documento}}:</strong> {{documento}}<br><strong>Telefone:</strong> {{telefone}}<br><strong>Endereço:</strong> {{endereco_completo}}</p>',
    '<p><strong>Período(s)</strong><br><strong>Saída:</strong> {{data_entrega}} &nbsp; <strong>Retorno:</strong> {{data_retirada}}</p>',
    '<p><strong>Produto(s)</strong></p>',
    '{{produtos_tabela}}',
    '<p><strong>Total Geral:</strong> {{total}}</p>',
    '<p><strong>Pagamento(s):</strong> {{forma_pagamento}}</p>',
    '<p><strong>Observações:</strong></p>',
  ].join('');

const resetTemplateForm = (kind = templateModalKind.value) => {
  templateModalKind.value = kind;
  Object.assign(templateForm, {
    id: null,
    name: '',
    body: kind === 'quote' ? quoteTemplateBody() : '',
    active: true,
    require_selfie: kind !== 'quote',
    metadata: {
      kind,
      required_documents: kind === 'quote' ? [] : [...DEFAULT_DOCUMENTS],
    },
  });
};

// Mensagem de envio do modelo: texto simples, template da Meta ou interativa.
const deliveryMessage = computed({
  get: () => templateForm.metadata?.delivery_message || null,
  set: value => {
    templateForm.metadata = {
      ...(templateForm.metadata || {}),
      delivery_message: value,
    };
  },
});

const requiredDocuments = computed({
  get: () => templateForm.metadata?.required_documents || [],
  set: value => {
    templateForm.metadata = {
      ...(templateForm.metadata || {}),
      required_documents: value,
    };
  },
});

const templateSearch = ref('');
const isSavingTemplate = ref(false);
const templateDeleteDialog = ref(null);
const pendingTemplateDelete = ref(null);
const isDeletingTemplate = ref(false);
const filteredTemplates = computed(() => {
  const term = templateSearch.value.trim().toLocaleLowerCase('pt-BR');
  if (!term) return visibleTemplates.value;
  return visibleTemplates.value.filter(template =>
    String(template.name || '')
      .toLocaleLowerCase('pt-BR')
      .includes(term)
  );
});
const customRequiredDocuments = computed(() =>
  requiredDocuments.value.filter(item => !DEFAULT_DOCUMENTS.includes(item))
);

const templateBodyFormat = computed({
  get: () =>
    templateForm.metadata?.body_format === 'html' ? 'html' : 'markdown',
  set: value => {
    const { body_format: _previous, ...rest } = templateForm.metadata || {};
    templateForm.metadata =
      value === 'html' ? { ...rest, body_format: 'html' } : rest;
  },
});

// HTML colado no editor de texto vira linhas de código (`<tag>`): o cliente veria o código.
const templateHasHtmlAsCode = computed(
  () =>
    templateBodyFormat.value === 'markdown' &&
    /`[^`\n]*<\s*[!a-z/][^`\n]*`/i.test(templateForm.body || '')
);

const MARKDOWN_ESCAPE = /\\([\\`*_{}[\]()#+\-.!>|])/g;
const convertTemplateBodyToHtml = () => {
  templateForm.body = String(templateForm.body || '')
    .split('\n')
    .map(line => {
      const trimmed = line.trim();
      const code = trimmed.match(/^`+([\s\S]*?)`+$/);
      return code ? code[1] : line.replace(MARKDOWN_ESCAPE, '$1');
    })
    .filter((line, index, lines) => line.trim() || lines[index - 1]?.trim())
    .join('\n');
  templateBodyFormat.value = 'html';
};

const insertIntoHtmlEditor = parameter => {
  const field = document.querySelector('[data-contract-html-editor] textarea');
  const body = templateForm.body || '';
  const start = field?.selectionStart ?? body.length;
  const end = field?.selectionEnd ?? body.length;
  templateForm.body = `${body.slice(0, start)}${parameter}${body.slice(end)}`;
  if (!field) return;
  requestAnimationFrame(() => {
    field.focus();
    field.setSelectionRange(start + parameter.length, start + parameter.length);
  });
};

const insertParameter = parameter => {
  if (templateBodyFormat.value === 'html') {
    insertIntoHtmlEditor(parameter);
    return;
  }
  const editor = document.querySelector('[data-contract-editor] .ProseMirror');
  if (!editor) return;

  editor.focus();
  document.execCommand('insertText', false, parameter);
  editor.dispatchEvent(new InputEvent('input', { bubbles: true }));
};

const toggleRequiredDocument = documentName => {
  if (requiredDocuments.value.includes(documentName)) {
    requiredDocuments.value = requiredDocuments.value.filter(
      item => item !== documentName
    );
    return;
  }

  requiredDocuments.value = [...requiredDocuments.value, documentName];
};

const addCustomDocument = () => {
  const value = customDocumentName.value.trim();
  if (!value || requiredDocuments.value.includes(value)) return;

  requiredDocuments.value = [...requiredDocuments.value, value];
  customDocumentName.value = '';
};

const selectedInvoiceProduct = computed(() =>
  productOptions.value.find(
    item => item.value === invoiceItemForm.product_value
  )
);

const selectedContractEditInvoiceProduct = computed(() =>
  contractEditProductOptions.value.find(
    item => item.value === contractEditInvoiceItemForm.product_value
  )
);

// Diárias multiplicam o valor de cada item (mesma regra do servidor). Os itens guardam o
// valor de uma diária; totais, orçamento e envio usam a versão já multiplicada.
const contractRentalDays = computed(() =>
  normalizeRentalDays(contractForm.rental_days)
);
const contractEditRentalDays = computed(() =>
  normalizeRentalDays(contractEditForm.rental_days)
);
const withRentalDays = (items, days) =>
  items.map(item => ({
    ...item,
    total: rentalItemTotal(
      item.quantity,
      item.unit_price,
      itemRentalDays(item, days)
    ),
  }));

// Item com valor fechado (ex.: frete): sem a marca, cobra por diária como antes.
const setChargePerDay = (item, chargePerDay) => {
  if (chargePerDay) delete item.skip_rental_days;
  else item.skip_rental_days = true;
};
const invoiceItemsWithDays = computed(() =>
  withRentalDays(invoiceItems.value, contractRentalDays.value)
);
const contractEditInvoiceItemsWithDays = computed(() =>
  withRentalDays(contractEditInvoiceItems.value, contractEditRentalDays.value)
);

const invoiceTotal = computed(() =>
  invoiceItemsWithDays.value.reduce(
    (total, item) => total + Number(item.total || 0),
    0
  )
);

const contractEditInvoiceTotal = computed(() =>
  contractEditInvoiceItemsWithDays.value.reduce(
    (total, item) => total + Number(item.total || 0),
    0
  )
);

// Enquanto não forem digitadas, as diárias acompanham as datas de entrega e retirada.
const rentalDaysTouched = reactive({ create: false, edit: false });
const syncRentalDays = (form, key) => {
  if (rentalDaysTouched[key]) return;
  const suggestion = suggestRentalDays(form.scheduled_at, form.pickup_at);
  if (suggestion) form.rental_days = String(suggestion);
};
const updateRentalDays = (form, key, value) => {
  rentalDaysTouched[key] = true;
  form.rental_days = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 3);
};
watch(
  () => [contractForm.scheduled_at, contractForm.pickup_at],
  () => syncRentalDays(contractForm, 'create')
);
watch(
  () => [contractEditForm.scheduled_at, contractEditForm.pickup_at],
  () => syncRentalDays(contractEditForm, 'edit')
);

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const toLocalDatetimeInput = value => {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const formatDateTime = value =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '-';

const toDateInputValue = date => {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const parseDateInputValue = value => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
};

const addMonths = (date, months) => {
  const value = new Date(date);
  value.setMonth(value.getMonth() + months);
  return value;
};

const startOfWeek = date => {
  const value = new Date(date);
  const day = value.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diff);
  return value;
};

const endOfMonth = date => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const formatDateLabel = value =>
  value ? parseDateInputValue(value).toLocaleDateString('pt-BR') : 'Selecionar';

const formatDateRangeLabel = (start, end) => {
  if (!start && !end) return 'Selecionar período';
  if (start && !end) return formatDateLabel(start);
  return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
};

const presetRange = presetKey => {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);

  if (presetKey === 'last7') start.setDate(today.getDate() - 6);
  if (presetKey === 'last30') start.setDate(today.getDate() - 29);
  if (presetKey === 'last3m') start.setMonth(today.getMonth() - 3);
  if (presetKey === 'last6m') start.setMonth(today.getMonth() - 6);
  if (presetKey === 'lastYear') {
    return {
      start: toDateInputValue(new Date(today.getFullYear() - 1, 0, 1)),
      end: toDateInputValue(new Date(today.getFullYear() - 1, 11, 31)),
    };
  }
  if (presetKey === 'thisWeek') {
    const weekStart = startOfWeek(today);
    return {
      start: toDateInputValue(weekStart),
      end: toDateInputValue(addDays(weekStart, 6)),
    };
  }
  if (presetKey === 'thisMonth') {
    return {
      start: toDateInputValue(
        new Date(today.getFullYear(), today.getMonth(), 1)
      ),
      end: toDateInputValue(endOfMonth(today)),
    };
  }

  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const setDateRangePreset = presetKey => {
  dateRangeDraft.preset = presetKey;
  if (presetKey === 'custom') return;

  const range = presetRange(presetKey);
  dateRangeDraft.start = range.start;
  dateRangeDraft.end = range.end;
  dateRangeDraft.month = range.start;
};

const openDateRangeFilter = filter => {
  activeDateRangeFilter.value =
    activeDateRangeFilter.value === filter.key ? '' : filter.key;
  dateRangeDraft.start = filters[filter.startKey] || '';
  dateRangeDraft.end = filters[filter.endKey] || '';
  dateRangeDraft.preset = 'custom';
  dateRangeDraft.month = dateRangeDraft.start || toDateInputValue(new Date());
};

const dateRangeButtonLabel = filter =>
  formatDateRangeLabel(filters[filter.startKey], filters[filter.endKey]);

const monthTitle = month =>
  month.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

const buildCalendarMonthFrom = (monthValue, offset = 0) => {
  const baseDate = parseDateInputValue(monthValue) || new Date();
  const month = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth() + offset,
    1
  );
  const gridStart = startOfWeek(month);
  const days = Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart, index);
    return {
      date,
      value: toDateInputValue(date),
      label: date.getDate(),
      isCurrentMonth: date.getMonth() === month.getMonth(),
    };
  });

  return { month, days };
};

const buildCalendarMonth = offset =>
  buildCalendarMonthFrom(dateRangeDraft.month, offset);

const calendarMonths = computed(() => [
  buildCalendarMonth(0),
  buildCalendarMonth(1),
]);

const stockCalendarMonth = computed(() =>
  buildCalendarMonthFrom(stockDateMonth.value)
);

const shiftCalendarMonth = direction => {
  const baseDate = parseDateInputValue(dateRangeDraft.month) || new Date();
  dateRangeDraft.month = toDateInputValue(addMonths(baseDate, direction));
};

const selectDateRangeDay = value => {
  dateRangeDraft.preset = 'custom';
  if (!dateRangeDraft.start || (dateRangeDraft.start && dateRangeDraft.end)) {
    dateRangeDraft.start = value;
    dateRangeDraft.end = '';
    return;
  }

  if (value < dateRangeDraft.start) {
    dateRangeDraft.end = dateRangeDraft.start;
    dateRangeDraft.start = value;
    return;
  }

  dateRangeDraft.end = value;
};

const calendarDayClass = day => {
  const isStart = day.value === dateRangeDraft.start;
  const isEnd = day.value === dateRangeDraft.end;
  const isInRange =
    dateRangeDraft.start &&
    dateRangeDraft.end &&
    day.value > dateRangeDraft.start &&
    day.value < dateRangeDraft.end;

  return [
    day.isCurrentMonth ? 'text-n-slate-12' : 'text-n-slate-8',
    isInRange ? 'bg-n-brand/20 text-n-slate-12' : '',
    isStart || isEnd
      ? 'bg-n-brand text-white hover:bg-n-brand'
      : 'hover:bg-n-alpha-2',
    isStart && !dateRangeDraft.end ? 'rounded-lg' : '',
    isStart && dateRangeDraft.end ? 'rounded-l-lg' : '',
    isEnd ? 'rounded-r-lg' : '',
  ];
};

const stockCalendarDayClass = day => [
  day.isCurrentMonth ? 'text-n-slate-12' : 'text-n-slate-8',
  day.value === stockDate.value
    ? 'rounded-lg bg-n-brand text-white hover:bg-n-brand'
    : 'rounded-lg hover:bg-n-alpha-2',
];

const openStockDatePicker = () => {
  stockDateMonth.value = stockDate.value || toDateInputValue(new Date());
  showStockDatePicker.value = !showStockDatePicker.value;
};

const shiftStockDateMonth = direction => {
  const baseDate = parseDateInputValue(stockDateMonth.value) || new Date();
  stockDateMonth.value = toDateInputValue(addMonths(baseDate, direction));
};

const selectStockDate = value => {
  stockDate.value = value;
  stockDateMonth.value = value;
  showStockDatePicker.value = false;
};

const parseCurrency = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0) / 100;
};

const invoiceItemPreview = computed(() => {
  const quantity = Math.max(Number(invoiceItemForm.quantity || 1), 1);
  const unitPrice = isCustomServiceItem.value
    ? parseCurrency(invoiceItemForm.unit_price)
    : parseCurrency(invoiceItemForm.unit_price) ||
      Number(selectedInvoiceProduct.value?.price || 0);
  return { quantity, unitPrice, total: quantity * unitPrice };
});

const maskCurrency = event => {
  invoiceItemForm.unit_price = formatCurrency(
    parseCurrency(event.target.value)
  );
};

const maskContractEditCurrency = event => {
  contractEditInvoiceItemForm.unit_price = formatCurrency(
    parseCurrency(event.target.value)
  );
};

const updateInvoiceItemQuantity = (items, index, value) => {
  const quantity = Math.max(Number(value || 1), 1);
  const item = items[index];
  if (!item) return;

  items[index] = {
    ...item,
    quantity,
    total: quantity * Number(item.unit_price || 0),
  };
};

const updateInvoiceItemUnitPrice = (items, index, event) => {
  const item = items[index];
  if (!item) return;

  const unitPrice = parseCurrency(event.target.value);
  event.target.value = formatCurrency(unitPrice);
  items[index] = {
    ...item,
    unit_price: unitPrice,
    total: Number(item.quantity || 1) * unitPrice,
  };
};

const syncSelectedProductPrice = () => {
  if (isCustomServiceItem.value) {
    invoiceItemForm.unit_price =
      invoiceItemForm.unit_price || formatCurrency(0);
    return;
  }

  invoiceItemForm.unit_price = selectedInvoiceProduct.value
    ? formatCurrency(selectedInvoiceProduct.value.price)
    : '';
};

const syncSelectedContractEditProductPrice = () => {
  if (isContractEditCustomServiceItem.value) {
    contractEditInvoiceItemForm.unit_price =
      contractEditInvoiceItemForm.unit_price || formatCurrency(0);
    return;
  }

  contractEditInvoiceItemForm.unit_price =
    selectedContractEditInvoiceProduct.value
      ? formatCurrency(selectedContractEditInvoiceProduct.value.price)
      : '';
};

const chooseInvoiceProduct = value => {
  invoiceItemForm.product_value = value;
  syncSelectedProductPrice();
  closeFormSelectMenu();
};

const chooseContractEditInvoiceProduct = value => {
  contractEditInvoiceItemForm.product_value = value;
  syncSelectedContractEditProductPrice();
  closeFormSelectMenu();
};

const addInvoiceItem = () => {
  if (!selectedInvoiceProduct.value || !canAddInvoiceItem.value) return;

  const { quantity, unitPrice } = invoiceItemPreview.value;
  const productName = isCustomServiceItem.value
    ? invoiceItemForm.custom_name.trim()
    : selectedInvoiceProduct.value.name;

  invoiceItems.value = [
    ...invoiceItems.value,
    {
      product_type: selectedInvoiceProduct.value.productType,
      product_id: selectedInvoiceProduct.value.productId,
      product_name: productName,
      quantity,
      unit_price: unitPrice,
      total: quantity * unitPrice,
    },
  ];

  Object.assign(invoiceItemForm, {
    product_value: '',
    custom_name: '',
    quantity: 1,
    unit_price: '',
  });
};

const findProductOptionForInvoiceItem = item => {
  const productType = item?.product_type || item?.productType;
  const productId = item?.product_id || item?.productId;
  const productName = item?.product_name || item?.productName;

  return contractEditProductOptions.value.find(option => {
    if (productType && option.productType !== productType) return false;
    if (productId) return Number(option.productId) === Number(productId);
    return productName && option.name === productName;
  });
};

const isBlankInvoiceItemValue = (key, value, fallback = {}) => {
  if (value === null || value === undefined || value === '') return true;
  if ((key === 'product_name' || key === 'productName') && value === 'Item') {
    return !!fallback.product_name;
  }
  if ((key === 'unit_price' || key === 'unitPrice') && Number(value) === 0) {
    return (
      Number(fallback.unit_price || fallback.unitPrice || fallback.total || 0) >
      0
    );
  }
  if (key === 'total' && Number(value) === 0) {
    return Number(fallback.total || 0) > 0;
  }
  return false;
};

const normalizeInvoiceItem = (item, fallback = {}) => {
  const mergedItem = {
    ...fallback,
    ...Object.fromEntries(
      Object.entries(item || {}).filter(
        ([key, value]) => !isBlankInvoiceItemValue(key, value, fallback)
      )
    ),
  };
  const productOption = findProductOptionForInvoiceItem(mergedItem);
  const rawQuantity = Number(mergedItem.quantity || 0);
  const quantity = rawQuantity > 0 ? rawQuantity : 1;
  const normalizedTotal = Number(mergedItem.total || fallback.total || 0);
  const unitPrice =
    Number(mergedItem.unit_price ?? mergedItem.unitPrice ?? 0) ||
    Number(productOption?.price || 0) ||
    normalizedTotal / quantity;

  const savedProductName = mergedItem.product_name || mergedItem.productName;
  const hasGenericProductName =
    !savedProductName || savedProductName === 'Item';

  return {
    product_type:
      mergedItem.product_type ||
      mergedItem.productType ||
      productOption?.productType ||
      '',
    product_id:
      mergedItem.product_id ||
      mergedItem.productId ||
      productOption?.productId ||
      null,
    product_name: hasGenericProductName
      ? productOption?.name || fallback.product_name || 'Item'
      : savedProductName,
    quantity,
    unit_price: unitPrice,
    total: Number(normalizedTotal || quantity * unitPrice),
    ...(skipsRentalDays(mergedItem) ? { skip_rental_days: true } : {}),
  };
};

const resetContractEditInvoiceItemForm = () => {
  Object.assign(contractEditInvoiceItemForm, {
    product_value: '',
    custom_name: '',
    quantity: 1,
    unit_price: '',
  });
};

const addContractEditInvoiceItem = () => {
  if (
    !selectedContractEditInvoiceProduct.value ||
    !canAddContractEditInvoiceItem.value
  ) {
    return;
  }

  const quantity = Math.max(
    Number(contractEditInvoiceItemForm.quantity || 1),
    1
  );
  const unitPrice = isContractEditCustomServiceItem.value
    ? parseCurrency(contractEditInvoiceItemForm.unit_price)
    : parseCurrency(contractEditInvoiceItemForm.unit_price) ||
      Number(selectedContractEditInvoiceProduct.value.price || 0);
  const productName = isContractEditCustomServiceItem.value
    ? contractEditInvoiceItemForm.custom_name.trim()
    : selectedContractEditInvoiceProduct.value.name;

  contractEditInvoiceItems.value = [
    ...contractEditInvoiceItems.value,
    {
      product_type: selectedContractEditInvoiceProduct.value.productType,
      product_id: selectedContractEditInvoiceProduct.value.productId,
      product_name: productName,
      quantity,
      unit_price: unitPrice,
      total: quantity * unitPrice,
    },
  ];

  resetContractEditInvoiceItemForm();
};

const removeInvoiceItem = index => {
  invoiceItems.value = invoiceItems.value.filter(
    (_, itemIndex) => itemIndex !== index
  );
};

const removeContractEditInvoiceItem = index => {
  contractEditInvoiceItems.value = contractEditInvoiceItems.value.filter(
    (_, itemIndex) => itemIndex !== index
  );
};

const fetchTemplates = async () => {
  const { data } = await ContractTemplatesAPI.get();
  templates.value = data;
  const firstContractTemplate = data.find(
    template => templateKind(template) === 'contract'
  );
  if (!selectedTemplateId.value && firstContractTemplate) {
    selectedTemplateId.value = firstContractTemplate.id;
    contractForm.talkvia_contract_template_id = firstContractTemplate.id;
  }
};

const endOfDayISO = value => {
  if (!value) return '';
  const date = parseDateInputValue(value);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

const startOfDayISO = value => {
  if (!value) return '';
  const date = parseDateInputValue(value);
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

const contractFilterParams = () => ({
  ...filters,
  start_at: filters.start_at ? startOfDayISO(filters.start_at) : undefined,
  end_at: filters.end_at ? endOfDayISO(filters.end_at) : undefined,
  pickup_start_at: filters.pickup_start_at
    ? startOfDayISO(filters.pickup_start_at)
    : undefined,
  pickup_end_at: filters.pickup_end_at
    ? endOfDayISO(filters.pickup_end_at)
    : undefined,
});

const fetchContracts = async () => {
  const { data } = await ContractsAPI.get(contractFilterParams());
  contracts.value = data.contracts || [];
  summary.value = data.summary || {};
};

const contractStatusFilterLabel = computed(
  () => statusOptions.find(option => option.value === filters.status)?.label
);

const contractStatusFilterItems = computed(() =>
  statusOptions.map(option => ({
    label: option.label,
    action: 'status_filter',
    value: option.value,
    icon: option.value === filters.status ? 'i-lucide-check' : '',
    isSelected: option.value === filters.status,
  }))
);

const applyContractStatusFilter = async ({ value }) => {
  filters.status = value;
  showContractStatusFilter.value = false;
  await fetchContracts();
};

const setDateRangeFilterRef = (key, element) => {
  if (element) {
    dateRangeFilterRefs.value[key] = element;
  } else {
    delete dateRangeFilterRefs.value[key];
  }
};

const handleDateRangeOutsidePointerDown = event => {
  if (showStockDatePicker.value) {
    const clickedInsideStockPicker = stockDatePickerRef.value?.contains(
      event.target
    );
    if (!clickedInsideStockPicker) showStockDatePicker.value = false;
  }

  if (openFormSelectMenu.value) {
    const activeSelectElement =
      formSelectMenuRefs.value[openFormSelectMenu.value];
    if (!activeSelectElement?.contains(event.target)) {
      closeFormSelectMenu();
    }
  }

  if (!activeDateRangeFilter.value) return;

  const activeFilterElement =
    dateRangeFilterRefs.value[activeDateRangeFilter.value];

  if (activeFilterElement?.contains(event.target)) return;

  activeDateRangeFilter.value = '';
};

const clearDateRangeFilter = async filter => {
  filters[filter.startKey] = '';
  filters[filter.endKey] = '';
  dateRangeDraft.start = '';
  dateRangeDraft.end = '';
  dateRangeDraft.preset = 'custom';
  activeDateRangeFilter.value = '';
  await fetchContracts();
};

const applyDateRangeFilter = async filter => {
  filters[filter.startKey] = dateRangeDraft.start;
  filters[filter.endKey] = dateRangeDraft.end || dateRangeDraft.start;
  activeDateRangeFilter.value = '';
  await fetchContracts();
};

const isDashboardLoading = ref(false);
const dashboardDateRange = reactive({ start: '', end: '' });

const fetchAllContractsForDashboard = async () => {
  isDashboardLoading.value = true;
  try {
    const params = { limit: 2000 };
    if (dashboardDateRange.start)
      params.created_start_at = new Date(
        dashboardDateRange.start
      ).toISOString();
    if (dashboardDateRange.end) {
      const end = new Date(dashboardDateRange.end);
      end.setHours(23, 59, 59, 999);
      params.created_end_at = end.toISOString();
    }
    const { data } = await ContractsAPI.get(params);
    allContracts.value = data.contracts || [];
  } finally {
    isDashboardLoading.value = false;
  }
};

const applyDashboardDateRange = () => fetchAllContractsForDashboard();

const setDashboardPreset = preset => {
  const now = new Date();
  if (preset === 'month') {
    dashboardDateRange.start = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    dashboardDateRange.end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
  } else if (preset === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    dashboardDateRange.start = d.toISOString().slice(0, 10);
    dashboardDateRange.end = now.toISOString().slice(0, 10);
  } else if (preset === 'year') {
    dashboardDateRange.start = new Date(now.getFullYear(), 0, 1)
      .toISOString()
      .slice(0, 10);
    dashboardDateRange.end = new Date(now.getFullYear(), 11, 31)
      .toISOString()
      .slice(0, 10);
  } else {
    dashboardDateRange.start = '';
    dashboardDateRange.end = '';
  }
  fetchAllContractsForDashboard();
};

const fetchProducts = async () => {
  const [creditResponse, inventoryResponse, groupsResponse] = await Promise.all(
    [
      TalkviaCreditProductsAPI.get(),
      TalkviaInventoryProductsAPI.get({ active: true }),
      TalkviaInventoryProductGroupsAPI.get({ active: true }),
    ]
  );
  creditProducts.value = creditResponse.data || [];
  inventoryProducts.value = inventoryResponse.data.products || [];
  inventoryProductGroups.value = groupsResponse.data || [];
};

const fetchStockByDate = async () => {
  const products = inventoryProducts.value.filter(p => p.active !== false);
  if (!products.length) return;
  isLoadingStock.value = true;
  try {
    const results = await Promise.all(
      products.map(p =>
        TalkviaInventoryProductsAPI.stockByDate(p.id, stockDate.value).then(
          r => r.data
        )
      )
    );
    stockByDateResults.value = results;
  } catch {
    stockByDateResults.value = [];
  } finally {
    isLoadingStock.value = false;
  }
};

const loadCustomPaymentMethods = () => {
  customPaymentMethods.value =
    currentAccount.value?.settings?.custom_payment_methods || [];
};

const saveNewPaymentMethod = async () => {
  const value = newPaymentMethodInput.value.trim();
  if (
    !value ||
    allPaymentMethods.value
      .map(m => m.toLowerCase())
      .includes(value.toLowerCase())
  )
    return;

  isSavingPaymentMethod.value = true;
  try {
    const updated = [...customPaymentMethods.value, value];
    await updateAccount({ custom_payment_methods: updated });
    customPaymentMethods.value = updated;
    newPaymentMethodInput.value = '';
    showAddPaymentInput.value = false;
    useAlert(copy.paymentMethodSaved);
  } finally {
    isSavingPaymentMethod.value = false;
  }
};

const removeCustomPaymentMethod = async method => {
  const updated = customPaymentMethods.value.filter(m => m !== method);
  await updateAccount({ custom_payment_methods: updated });
  customPaymentMethods.value = updated;
  if (contractForm.payment_method === method) contractForm.payment_method = '';
};

const searchContacts = async () => {
  if (contactSearch.value.trim().length < 2) {
    contactResults.value = [];
    return;
  }

  isSearchingContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(contactSearch.value, 1);
    contactResults.value = data.payload || [];
  } finally {
    isSearchingContacts.value = false;
  }
};

const contactAttribute = (contact, keys) => {
  const source = {
    ...(contact?.additional_attributes || {}),
    ...(contact?.custom_attributes || {}),
  };
  return keys.map(key => source[key]).find(value => value) || '';
};

const contactDocuments = contact =>
  [
    {
      type: 'cpf',
      label: 'CPF',
      value: contactAttribute(contact, ['cpf', 'CPF', 'document', 'documento']),
    },
    {
      type: 'cnpj',
      label: 'CNPJ',
      value: contactAttribute(contact, ['cnpj', 'CNPJ']),
    },
  ].filter(document => document.value);

const contactCpf = contact => contactDocuments(contact)[0]?.value || '';

const selectedContactDocuments = computed(() =>
  contactDocuments(selectedContact.value)
);

const recipientNameForDocument = (contact, documentType) => {
  if (documentType !== 'cnpj') return contact?.name || '';

  return (
    contactAttribute(contact, [
      'razao_social',
      'razaoSocial',
      'nome_fantasia',
      'nomeFantasia',
    ]) ||
    contact?.name ||
    ''
  );
};

const selectRecipientDocument = documentType => {
  const document = selectedContactDocuments.value.find(
    item => item.type === documentType
  );
  if (!document) return;

  selectedDocumentType.value = document.type;
  contractForm.recipient_cpf = document.value;
  contractForm.recipient_name = recipientNameForDocument(
    selectedContact.value,
    document.type
  );
};

const extractContactAddress = contact => ({
  recipient_zip_code: contactAttribute(contact, [
    'cep',
    'zip_code',
    'zipCode',
    'zipcode',
    'postal_code',
  ]),
  recipient_address: contactAttribute(contact, [
    'endereco',
    'address',
    'addressLine',
    'address_line',
    'street',
    'logradouro',
  ]),
  recipient_address_number: contactAttribute(contact, [
    'numero',
    'number',
    'addressNumber',
    'address_number',
  ]),
  recipient_address_complement: contactAttribute(contact, [
    'complemento',
    'complement',
    'addressComplement',
    'address_complement',
  ]),
  recipient_neighborhood: contactAttribute(contact, [
    'bairro',
    'neighborhood',
    'district',
  ]),
  recipient_city: contactAttribute(contact, ['cidade', 'city']),
  recipient_state: contactAttribute(contact, ['uf', 'state', 'province']),
});

const applyAddressToContract = address => {
  Object.assign(contractForm, address);
};

const extractContractAddress = contract => {
  const address = contract.metadata?.recipient_address || {};
  return {
    recipient_zip_code: address.zip_code || '',
    recipient_address: address.address || '',
    recipient_address_number: address.number || '',
    recipient_address_complement: address.complement || '',
    recipient_neighborhood: address.neighborhood || '',
    recipient_city: address.city || '',
    recipient_state: address.state || '',
    recipient_address_source:
      contract.metadata?.recipient_address_source || 'contract',
  };
};

const cleanZipCode = value => String(value || '').replace(/\D/g, '');

const applyZipCodeAddress = (form, zipCode, data) => {
  Object.assign(form, {
    recipient_zip_code: `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`,
    recipient_address: data.logradouro || form.recipient_address,
    recipient_neighborhood: data.bairro || form.recipient_neighborhood,
    recipient_city: data.localidade || form.recipient_city,
    recipient_state: data.uf || form.recipient_state,
    recipient_address_complement:
      data.complemento || form.recipient_address_complement,
  });
};

const lookupZipCodeForForm = async ({
  form,
  force = false,
  isFetchingRef,
  lastLookupRef,
}) => {
  const zipCode = cleanZipCode(form.recipient_zip_code);
  if (zipCode.length !== 8) return;
  if (!force && zipCode === lastLookupRef.value) return;

  isFetchingRef.value = true;
  lastLookupRef.value = zipCode;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
    const data = await response.json();
    if (!response.ok || data.erro) throw new Error(copy.zipCodeNotFound);

    applyZipCodeAddress(form, zipCode, data);
  } catch {
    useAlert(copy.zipCodeNotFound);
  } finally {
    isFetchingRef.value = false;
  }
};

const lookupContractZipCode = async (force = false) => {
  if (useContactAddress.value) return;

  await lookupZipCodeForForm({
    form: contractForm,
    force,
    isFetchingRef: isFetchingContractZipCode,
    lastLookupRef: lastContractZipCodeLookup,
  });
};

const lookupContractEditZipCode = async (force = false) => {
  await lookupZipCodeForForm({
    form: contractEditForm,
    force,
    isFetchingRef: isFetchingContractEditZipCode,
    lastLookupRef: lastContractEditZipCodeLookup,
  });
};

const setUseContactAddress = shouldUseContactAddress => {
  useContactAddress.value = shouldUseContactAddress;
  contractForm.recipient_address_source = shouldUseContactAddress
    ? 'contact'
    : 'contract';
  if (shouldUseContactAddress) {
    applyAddressToContract(selectedContactAddress.value);
  }
};

const selectContact = contact => {
  selectedContact.value = contact;
  contractForm.contact_id = contact.id;
  contractForm.recipient_phone = contact.phone_number || '';
  const documents = contactDocuments(contact);
  const preferredType =
    contactAttribute(contact, ['tipo_pessoa', 'tipoPessoa']) === 'pj'
      ? 'cnpj'
      : 'cpf';
  const selectedDocument =
    documents.find(document => document.type === preferredType) || documents[0];
  selectedDocumentType.value = selectedDocument?.type || '';
  contractForm.recipient_cpf = selectedDocument?.value || '';
  contractForm.recipient_name = recipientNameForDocument(
    contact,
    selectedDocument?.type
  );
  selectedContactAddress.value = extractContactAddress(contact);
  if (useContactAddress.value) {
    applyAddressToContract(selectedContactAddress.value);
  }
  // O contato escolhido aparece como cartão; a busca fica limpa para uma troca.
  contactSearch.value = '';
  contactResults.value = [];
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  contractForm.contact_id = '';
  contactSearch.value = '';
  contactResults.value = [];
};

const contactInitials = contact =>
  String(contact?.name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || '?';

const selectedContactDocumentLabel = computed(() => {
  const document = selectedContactDocuments.value.find(
    item => item.type === selectedDocumentType.value
  );
  return document ? `${document.label} ${document.value}` : '';
});

const selectedContactAddressSummary = computed(() => {
  const address = selectedContactAddress.value || {};
  const street = [address.recipient_address, address.recipient_address_number]
    .filter(Boolean)
    .join(', ');
  const cityState = [address.recipient_city, address.recipient_state]
    .filter(Boolean)
    .join(' - ');
  return [
    street,
    address.recipient_address_complement,
    address.recipient_neighborhood,
    cityState,
    address.recipient_zip_code && `CEP ${address.recipient_zip_code}`,
  ]
    .filter(Boolean)
    .join(' · ');
});

const createContractContact = async contact => {
  if (isCreatingContact.value) return;
  isCreatingContact.value = true;
  try {
    const createdContact = await store.dispatch('contacts/create', contact);
    selectContact(createdContact);
    createContactDialogRef.value?.onSuccess();
    useAlert('Contato cadastrado e selecionado para o contrato.');
  } catch (error) {
    if (
      error instanceof DuplicateContactException &&
      error.data?.includes('phone_number')
    ) {
      useAlert(
        'Este telefone já está cadastrado. Busque e selecione o contato existente.'
      );
    } else if (
      error instanceof DuplicateContactException &&
      error.data?.includes('email')
    ) {
      useAlert(
        'Este e-mail já está cadastrado. Busque e selecione o contato existente.'
      );
    } else {
      useAlert(
        'Não foi possível cadastrar o contato. Confira os dados e tente novamente.'
      );
    }
  } finally {
    isCreatingContact.value = false;
  }
};

const quotesPanel = ref();
const sourceQuoteId = ref(null);

const openGenerateContractModal = () => {
  sourceQuoteId.value = null;
  contractForm.talkvia_contract_template_id =
    contractForm.talkvia_contract_template_id || selectedTemplateId.value;
  contractStep.value = 0;
  contractStepMessage.value = '';
  resetGeneratorPosition();
  showGenerateContractModal.value = true;
  loadSignatureLevels();
};

// Conversa de origem quando o assistente é aberto pelo painel do contato: o contrato nasce
// vinculado a ela e o envio do link já sugere a caixa dela.
const embeddedConversation = ref(null);
const embeddedSendOptions = () =>
  embeddedConversation.value?.inboxId
    ? { inboxId: embeddedConversation.value.inboxId }
    : {};
const withEmbeddedConversation = payload =>
  embeddedConversation.value?.id &&
  Number(payload?.inbox_id) === Number(embeddedConversation.value.inboxId)
    ? { ...payload, conversation_id: embeddedConversation.value.id }
    : payload;

// Recarrega modelos e produtos a cada abertura (sem repetir chamadas já em andamento).
let generatorDataPromise = null;
const loadGeneratorData = () => {
  generatorDataPromise ||= (async () => {
    isLoading.value = true;
    try {
      // Sem o módulo de estoque a lista de produtos pode falhar; os modelos bastam.
      const [templatesResult] = await Promise.allSettled([
        fetchTemplates(),
        fetchProducts(),
      ]);
      loadCustomPaymentMethods();
      if (templatesResult.status === 'rejected') {
        useAlert(
          'Não foi possível carregar os modelos de contrato. Feche e abra novamente.'
        );
      }
    } finally {
      isLoading.value = false;
      generatorDataPromise = null;
    }
  })();
  return generatorDataPromise;
};

const openGenerator = async ({ contact, conversationId, inboxId } = {}) => {
  embeddedConversation.value = conversationId
    ? { id: conversationId, inboxId }
    : null;
  if (contact?.id && Number(selectedContact.value?.id) !== Number(contact.id)) {
    // Outro cliente: não reaproveita itens do rascunho anterior.
    invoiceItems.value = [];
    selectContact(contact);
  }
  openGenerateContractModal();
  await loadGeneratorData();
};

const convertQuote = async quote => {
  try {
    const { data } = await ContactsAPI.show(quote.contact_id);
    await openGenerator({ contact: data.payload || data });
    sourceQuoteId.value = quote.id;
    rentalDaysTouched.create = true;
    const payload = quote.payload;
    Object.assign(contractForm, {
      recipient_name: payload.recipient_name || '',
      recipient_cpf: payload.recipient_cpf || '',
      recipient_phone: payload.recipient_phone || '',
      scheduled_at: payload.scheduled_at || '',
      pickup_at: payload.pickup_at || '',
      payment_method: payload.payment_method || '',
      rental_days: String(payload.rental_days || 1),
      recipient_address_source: 'contract',
    });
    applyAddressToContract(
      extractContractAddress({
        metadata: { recipient_address: payload.recipient_address || {} },
      })
    );
    invoiceItems.value = quoteContractItems(payload.invoice_items);
  } catch {
    useAlert('Não foi possível preparar o contrato deste orçamento.');
  }
};

const closeGenerateContractModal = () => {
  if (isGeneratingContract.value) return;
  showGenerateContractModal.value = false;
  closeFormSelectMenu();
};

const refresh = async () => {
  isLoading.value = true;
  try {
    await Promise.all([fetchTemplates(), fetchContracts(), fetchProducts()]);
    loadCustomPaymentMethods();
  } finally {
    isLoading.value = false;
  }
};

const openTemplateModal = (template = null, kind = templateModalKind.value) => {
  if (template) {
    templateModalKind.value = templateKind(template);
    Object.assign(templateForm, {
      id: template.id,
      name: template.name,
      body: template.body,
      active: template.active,
      require_selfie: template.require_selfie !== false,
      metadata: {
        ...(template.metadata || {}),
        kind: templateKind(template),
        required_documents:
          template.metadata?.required_documents ||
          (templateKind(template) === 'quote' ? [] : [...DEFAULT_DOCUMENTS]),
      },
    });
  } else {
    resetTemplateForm(kind);
  }
  showTemplateModal.value = true;
};

const saveTemplate = async () => {
  if (isSavingTemplate.value) return;
  isSavingTemplate.value = true;
  templateForm.metadata = {
    ...(templateForm.metadata || {}),
    kind: templateModalKind.value,
  };
  const payload = {
    name: templateForm.name,
    body: templateForm.body,
    active: templateForm.active,
    require_selfie: templateForm.require_selfie,
    metadata: templateForm.metadata,
  };
  try {
    if (templateForm.id) {
      await ContractTemplatesAPI.update(templateForm.id, payload);
      useAlert('Modelo atualizado.');
    } else {
      await ContractTemplatesAPI.create(payload);
      useAlert('Modelo criado.');
    }
    resetTemplateForm();
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível salvar o modelo. Revise os dados e tente novamente.'
    );
  } finally {
    isSavingTemplate.value = false;
  }
};

const removeTemplate = template => {
  pendingTemplateDelete.value = template;
  templateDeleteDialog.value?.open();
};

// Excluir modelo exige confirmação; contratos já gerados preservam o conteúdo.
const confirmRemoveTemplate = async () => {
  const template = pendingTemplateDelete.value;
  if (!template || isDeletingTemplate.value) return;
  isDeletingTemplate.value = true;
  try {
    await ContractTemplatesAPI.delete(template.id);
    if (templateForm.id === template.id) resetTemplateForm();
    templateDeleteDialog.value?.close();
    useAlert('Modelo excluído.');
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível excluir o modelo.'
    );
  } finally {
    isDeletingTemplate.value = false;
    pendingTemplateDelete.value = null;
  }
};

const openQuoteDialog = () => {
  contractSendDialogRef.value?.openQuote(
    {
      contact_id: contractForm.contact_id,
      recipient_name: contractForm.recipient_name,
      recipient_cpf: contractForm.recipient_cpf,
      recipient_phone: contractForm.recipient_phone,
      scheduled_at: contractForm.scheduled_at,
      pickup_at: contractForm.pickup_at,
      payment_method: contractForm.payment_method,
      rental_days: contractRentalDays.value,
      invoice_items: invoiceItemsWithDays.value,
      recipient_address: {
        zip_code: contractForm.recipient_zip_code,
        address: contractForm.recipient_address,
        number: contractForm.recipient_address_number,
        complement: contractForm.recipient_address_complement,
        neighborhood: contractForm.recipient_neighborhood,
        city: contractForm.recipient_city,
        state: contractForm.recipient_state,
      },
    },
    embeddedSendOptions()
  );
};

function openContractQuoteDialog(contract) {
  contractSendDialogRef.value?.openQuote(
    {
      contact_id: contract.contact_id,
      recipient_name: contract.recipient_name,
      recipient_cpf: contract.recipient_cpf,
      recipient_phone: contract.recipient_phone,
      scheduled_at: contract.scheduled_at,
      pickup_at: contract.pickup_at,
      payment_method: contract.payment_method,
      rental_days: normalizeRentalDays(contract.rental_days),
      invoice_items: contractQuoteInvoiceItems(contract),
      quote_number: contract.id,
      contract_id: contract.id,
      public_url: contract.public_url,
      signature_url: contract.signed_at ? '' : contract.public_url,
      recipient_address: contract.metadata?.recipient_address || {},
    },
    embeddedSendOptions()
  );
}

const goToContractStep = async nextStep => {
  if (isGeneratingContract.value) return;
  contractStepMessage.value = '';
  if (nextStep > contractStep.value) {
    for (let step = 0; step < nextStep; step += 1) {
      const error = contractStepError(step, contractForm, invoiceItems.value);
      if (error) {
        contractStep.value = step;
        contractStepMessage.value = error;
        return;
      }
    }
  }
  contractStep.value = Math.max(0, Math.min(3, nextStep));
  closeFormSelectMenu();
  await nextTick();
  contractStepBody.value?.scrollTo({ top: 0 });
};

const generateContract = async ({ sendToContact = false } = {}) => {
  if (isGeneratingContract.value || contractStep.value !== 3) return;
  for (let step = 0; step < 3; step += 1) {
    const error = contractStepError(step, contractForm, invoiceItems.value);
    if (error) {
      contractStep.value = step;
      contractStepMessage.value = error;
      return;
    }
  }
  contractForm.talkvia_contract_template_id =
    contractForm.talkvia_contract_template_id || selectedTemplateId.value;
  if (!contractForm.talkvia_contract_template_id) {
    contractStep.value = 0;
    contractStepMessage.value =
      'Escolha um modelo para gerar o contrato. Para enviar somente orçamento, avance sem modelo.';
    return;
  }
  if (
    contractForm.signature_level !== 'simple' &&
    !/^(55)?\d{10,11}$/.test(
      String(contractForm.recipient_phone || '').replace(/\D/g, '')
    )
  ) {
    contractStepMessage.value =
      'Para a assinatura reforçada ou biométrica, informe o celular do cliente com DDD: o código de confirmação vai pelo WhatsApp.';
    return;
  }
  isGeneratingContract.value = true;
  let createdContract;
  try {
    const { data } = await ContractsAPI.create({
      ...contractForm,
      ...(sourceQuoteId.value ? { quote_id: sourceQuoteId.value } : {}),
      invoice_items: invoiceItems.value,
      ...(embeddedConversation.value?.id
        ? {
            conversation_id: embeddedConversation.value.id,
            source: 'contact_panel',
          }
        : {}),
    });
    createdContract = data;
    contracts.value = [data, ...contracts.value];
    invoiceItems.value = [];
  } catch (error) {
    if (error?.response?.data?.allowance)
      contractAllowance.value = error.response.data.allowance;
    contractStepMessage.value =
      error?.response?.data?.error ||
      'Não foi possível gerar o contrato. Confira os dados e tente novamente.';
    return;
  } finally {
    isGeneratingContract.value = false;
  }
  closeGenerateContractModal();
  await nextTick();
  if (sendToContact)
    contractSendDialogRef.value?.open(createdContract, embeddedSendOptions());
  else openContractQuoteDialog(createdContract);
  useAlert('Contrato gerado.');
  quotesPanel.value?.load();
  if (props.generatorOnly) {
    emit('created', createdContract);
    return;
  }
  await fetchContracts();
};

const sendLink = async contract => {
  contractSendDialogRef.value?.open(contract);
};

const isSendingDocument = ref(false);
const handleSendContractLink = async ({ contract, payload }) => {
  if (isSendingDocument.value) return;
  isSendingDocument.value = true;
  try {
    await ContractsAPI.sendLink(contract.id, withEmbeddedConversation(payload));
    contractSendDialogRef.value?.close();
    useAlert('Link enviado na conversa.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível enviar. Confira os dados e tente novamente.'
    );
  } finally {
    isSendingDocument.value = false;
  }
};

// Dados fictícios da prévia dos modelos (mesmos do servidor para contratos).
const templatePreviewPayload = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = date => date.toISOString().slice(0, 10);
  const pickup = new Date(tomorrow);
  pickup.setDate(pickup.getDate() + 2);
  return {
    quote_number: '1001',
    recipient_name: 'Tom Martins',
    recipient_cpf: '390.533.447-05',
    recipient_phone: '+55 19 99999-0000',
    payment_method: 'Pix',
    scheduled_at: `${day(tomorrow)}T09:00`,
    pickup_at: `${day(pickup)}T09:00`,
    rental_days: 2,
    recipient_address: {
      zip_code: '13010-000',
      address: 'Rua Barão de Jaguara',
      number: '100',
      neighborhood: 'Centro',
      city: 'Campinas',
      state: 'SP',
    },
    invoice_items: [
      {
        product_name: 'Mesa plástica',
        quantity: 6,
        unit_price: 14,
        total: 168,
      },
      {
        product_name: 'Frete',
        quantity: 1,
        unit_price: 100,
        total: 100,
        skip_rental_days: true,
      },
    ],
  };
};

// Página do contrato com o mesmo corpo sanitizado da assinatura, sem scripts (iframe isolado).
const contractPreviewDocument = bodyHtml =>
  `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><style>
body{margin:0;background:#f8fafc;font-family:Inter,system-ui,-apple-system,sans-serif;color:#111827}
main{max-width:760px;margin:24px auto;padding:32px 36px;background:#fff;border:1px solid #e2e8f0;border-radius:12px;line-height:1.6;font-size:15px}
h1,h2,h3,h4{line-height:1.3;margin:1.2em 0 .5em}p{margin:0 0 .9em}ul,ol{padding-left:1.4em}
table{width:100%;border-collapse:collapse;margin:1em 0}th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left}
code{background:#f1f5f9;border-radius:4px;padding:1px 4px;font-size:.9em}blockquote{border-left:3px solid #cbd5e1;margin:0;padding-left:12px;color:#475569}
</style></head><body><main>${bodyHtml}</main></body></html>`;

const openTemplatePreview = async () => {
  if (!templateForm.body) return;
  templatePreviewRequestId += 1;
  const requestId = templatePreviewRequestId;
  Object.assign(templatePreview, {
    loading: true,
    html: '',
    warnings: [],
    error: '',
  });
  templatePreviewDialog.value?.open();
  try {
    if (templateModalKind.value === 'quote') {
      const payload = templatePreviewPayload();
      templatePreview.html = buildCustomQuoteDocument({
        payload,
        bodyHtml: renderQuoteTemplateBody(
          templateForm.body,
          quoteTemplateValues(payload)
        ),
      });
    } else {
      const { data } = await ContractTemplatesAPI.preview(
        templateForm.body,
        templateBodyFormat.value
      );
      if (requestId !== templatePreviewRequestId) return;
      templatePreview.html =
        data?.format === 'html'
          ? data.html || ''
          : contractPreviewDocument(data?.html || '');
      templatePreview.warnings = Array.isArray(data?.warnings)
        ? data.warnings
        : [];
    }
  } catch {
    if (requestId === templatePreviewRequestId)
      templatePreview.error = copy.previewError;
  } finally {
    if (requestId === templatePreviewRequestId) templatePreview.loading = false;
  }
};

const quotePdfDocument = payload => {
  const selectedQuoteTemplate = quoteTemplates.value.find(
    template => Number(template.id) === Number(payload.quote_template_id)
  );
  // Custom templates keep their saved body; only the default uses the layout.
  if (selectedQuoteTemplate?.body) {
    return buildCustomQuoteDocument({
      payload,
      bodyHtml: renderQuoteTemplateBody(
        selectedQuoteTemplate.body,
        quoteTemplateValues(payload)
      ),
    });
  }

  return buildDefaultQuoteDocument({ payload, account: currentAccount.value });
};

const triggerPrint = targetWindow => {
  window.setTimeout(() => {
    try {
      targetWindow.focus();
      targetWindow.print();
    } catch {
      useAlert('Não foi possível iniciar a impressão do PDF.');
    }
  }, 250);
};

const generateQuotePdfWithIframe = payload => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.right = '0';
  iframe.style.bottom = '0';

  iframe.onload = () => {
    if (!iframe.contentWindow) {
      useAlert('Não foi possível iniciar a impressão do PDF.');
      iframe.remove();
      return;
    }

    triggerPrint(iframe.contentWindow);
    window.setTimeout(() => iframe.remove(), 1500);
  };

  iframe.srcdoc = quotePdfDocument(payload);
  document.body.appendChild(iframe);
};

const hydrateQuotePdfPayload = async payload => {
  const normalizedPayload = {
    ...payload,
    invoice_items: quoteInvoiceItems(payload),
  };
  if (!payload?.contract_id) return normalizedPayload;

  const { data: contract } = await ContractsAPI.show(payload.contract_id);
  return {
    ...normalizedPayload,
    rental_days: normalizeRentalDays(contract.rental_days),
    invoice_items: contractQuoteInvoiceItems(contract),
    signature_url: contract.signed_at ? '' : contract.public_url,
  };
};

const handleGenerateQuotePdf = async payload => {
  const printWindow = window.open('', '_blank');
  try {
    const hydratedPayload = await hydrateQuotePdfPayload(payload);
    if (!printWindow) {
      generateQuotePdfWithIframe(hydratedPayload);
      return;
    }

    printWindow.document.open();
    printWindow.document.write(quotePdfDocument(hydratedPayload));
    printWindow.document.close();
    triggerPrint(printWindow);
  } catch {
    printWindow?.close();
    useAlert(
      'Não foi possível carregar os dados do contrato para gerar o PDF. Tente novamente.'
    );
  }
};

const handleGenerateReceipt = async payload => {
  if (isSendingDocument.value) return;
  isSendingDocument.value = true;
  try {
    const { data } = await ContractsAPI.generateReceipt(payload);
    data.files.forEach(file => {
      const bytes = Uint8Array.from(atob(file.data), character =>
        character.charCodeAt(0)
      );
      const url = URL.createObjectURL(
        new Blob([bytes], { type: file.content_type })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    });
    useAlert('Recibo gerado para download.');
  } catch {
    useAlert(
      'Não foi possível gerar o recibo. Confira os dados do pagamento e tente novamente.'
    );
  } finally {
    isSendingDocument.value = false;
  }
};

// Anexa o orçamento (PDF ou PNG) gerado do mesmo documento do botão Gerar PDF.
const withQuoteAttachment = async payload => {
  const format = payload.quote_attachment_format;
  if (!['pdf', 'png'].includes(format)) return payload;

  const hydratedPayload = await hydrateQuotePdfPayload(payload);
  const quoteFiles = await renderQuoteAttachment({
    html: quotePdfDocument(hydratedPayload),
    format,
    name: quoteDocumentTitle(hydratedPayload),
  });
  return { ...hydratedPayload, quote_files: quoteFiles };
};

const handleSendQuote = async payload => {
  if (isSendingDocument.value) return;
  isSendingDocument.value = true;
  try {
    if (payload.document_kind === 'receipt') {
      await ContractsAPI.sendReceipt(payload);
    } else {
      await ContractsAPI.sendQuote(await withQuoteAttachment(payload));
    }
    contractSendDialogRef.value?.close();
    useAlert(
      payload.document_kind === 'receipt'
        ? 'Recibo enviado na conversa.'
        : 'Orçamento enviado na conversa.'
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível enviar. Confira a caixa e os dados informados e tente novamente.'
    );
  } finally {
    isSendingDocument.value = false;
  }
};

const requestRemoveContract = contract => {
  contractPendingDelete.value = contract;
  deleteContractDialogRef.value?.open();
};

const closeDeleteContractDialog = () => {
  contractPendingDelete.value = null;
};

const confirmRemoveContract = async () => {
  if (!contractPendingDelete.value) return;

  isDeletingContract.value = true;
  try {
    await ContractsAPI.delete(contractPendingDelete.value.id);
    useAlert('Contrato excluído.');
    deleteContractDialogRef.value?.close();
    await fetchContracts();
  } catch {
    useAlert('Não foi possível excluir o contrato.');
  } finally {
    isDeletingContract.value = false;
  }
};

const openContract = contract => {
  window.open(contract.public_url, '_blank', 'noopener,noreferrer');
};

const openDocumentsModal = contract => {
  selectedDocuments.value = contract.metadata?.document_uploads || [];
  selectedDocumentsName.value = contract.recipient_name;
  showDocumentsModal.value = true;
};

const documentDownloadHref = doc =>
  `data:${doc.content_type};base64,${doc.data}`;

const openConversation = contract => {
  if (!contract.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: contract.conversation_id },
  });
};

// Reabrir arquiva a assinatura anterior: o motivo entra na trilha do contrato.
const reopenDialogRef = ref(null);
const contractPendingReopen = ref(null);
const reopenReason = ref('');

const openReopenDialog = contract => {
  contractPendingReopen.value = contract;
  reopenReason.value = '';
  reopenDialogRef.value?.open();
};

const closeReopenDialog = () => {
  contractPendingReopen.value = null;
  reopenReason.value = '';
};

const updateContractStatus = async (
  contract,
  nextStatus,
  reason = '',
  confirmed = false
) => {
  if (!nextStatus || nextStatus === contract.status) return;
  if (nextStatus === 'pending' && contract.signed_at && !confirmed) {
    openReopenDialog(contract);
    return;
  }

  updatingStatusContractId.value = contract.id;
  try {
    const { data } = await ContractsAPI.updateStatus(
      contract.id,
      nextStatus,
      reason
    );
    Object.assign(contract, data);
    useAlert(
      nextStatus === 'pending' ? copy.pendingReopened : copy.statusUpdated
    );
    await fetchContracts();
  } catch {
    useAlert('Não foi possível alterar o status do contrato.');
  } finally {
    updatingStatusContractId.value = null;
  }
};

const confirmReopenContract = async () => {
  const contract = contractPendingReopen.value;
  if (!contract) return;
  reopenDialogRef.value?.close();
  await updateContractStatus(contract, 'pending', reopenReason.value, true);
};

const statusBadgeClass = status => {
  const classes = {
    pending: 'border-n-amber-5 bg-n-amber-3 text-n-amber-11',
    signed: 'border-n-teal-5 bg-n-teal-3 text-n-teal-11',
    expired: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
    awaiting_delivery: 'border-n-blue-5 bg-n-blue-3 text-n-blue-11',
    completed: 'border-n-green-5 bg-n-green-3 text-n-green-11',
  };
  return classes[status] || 'border-n-slate-5 bg-n-slate-3 text-n-slate-11';
};

// Dashboard metrics
const dashboardMetrics = computed(() => {
  const src = allContracts.value;
  const totalValue = src.reduce(
    (sum, c) => sum + Number(c.metadata?.invoice_total || 0),
    0
  );

  const byStatus = contractStatusOptions.map(opt => ({
    label: opt.label,
    value: opt.value,
    count: src.filter(c => c.status === opt.value).length,
  }));

  const templateMap = {};
  src.forEach(c => {
    const key = c.template_name || 'Sem modelo';
    templateMap[key] = (templateMap[key] || 0) + 1;
  });
  const byTemplate = Object.entries(templateMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const paymentMap = {};
  src.forEach(c => {
    const key = c.payment_method || 'Não informado';
    paymentMap[key] = (paymentMap[key] || 0) + 1;
  });
  const byPayment = Object.entries(paymentMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const withDelivery = src.filter(c => c.scheduled_at).length;
  const overdue = src.filter(
    c =>
      c.scheduled_at &&
      new Date(c.scheduled_at) < new Date() &&
      !['completed', 'cancelled'].includes(c.status)
  ).length;

  return {
    total: src.length,
    totalValue,
    byStatus,
    byTemplate,
    byPayment,
    withDelivery,
    overdue,
  };
});

const openDashboard = async () => {
  showDashboard.value = true;
  isDashboardExpanded.value = false;
  allContracts.value = [];
  dashboardDateRange.start = '';
  dashboardDateRange.end = '';
  await fetchAllContractsForDashboard();
};

const canEditContract = contract =>
  contract && !['signed', 'completed'].includes(contract.status);

// Carrega a disponibilidade dos níveis sem alterar o nível já gravado no contrato.
const loadContractEditSignatureLevels = async () => {
  try {
    const { data } = await ContractsAPI.signatureLevels();
    signatureLevels.value = data.levels || {};
    contractAllowance.value = data.allowance || null;
  } catch {
    signatureLevels.value = {};
  }
};

const openContractEditModal = contract => {
  if (!canEditContract(contract)) {
    useAlert(copy.contractEditLocked);
    return;
  }

  editingContract.value = contract;
  Object.assign(contractEditForm, {
    recipient_name: contract.recipient_name || '',
    recipient_cpf: contract.recipient_cpf || '',
    recipient_phone: contract.recipient_phone || '',
    ...extractContractAddress(contract),
    scheduled_at: toLocalDatetimeInput(contract.scheduled_at),
    pickup_at: toLocalDatetimeInput(contract.pickup_at),
    payment_method: contract.payment_method || '',
    talkvia_contract_template_id: contract.talkvia_contract_template_id || '',
    signature_level: contract.signature_level || 'simple',
    rental_days: String(normalizeRentalDays(contract.rental_days)),
  });
  rentalDaysTouched.edit = true;
  loadContractEditSignatureLevels();
  const fallbackInvoiceItem = {
    product_type: contract.product_type,
    product_id: contract.product_id,
    product_name: contract.product_name,
    quantity: 1,
    total: contract.metadata?.invoice_total,
  };
  const sourceInvoiceItems = contract.metadata?.invoice_items?.length
    ? contract.metadata.invoice_items
    : [fallbackInvoiceItem];
  contractEditInvoiceItems.value = sourceInvoiceItems.map(item =>
    normalizeInvoiceItem(item, fallbackInvoiceItem)
  );
  resetContractEditInvoiceItemForm();
  showContractEditModal.value = true;
};

const contractStatusLabel = status =>
  statusOptions.find(option => option.value === status)?.label || status || '-';

const toggleContractActionsMenu = contractId => {
  openContractStatusMenuId.value = null;
  openContractActionsId.value =
    openContractActionsId.value === contractId ? null : contractId;
};

const toggleContractStatusMenu = contractId => {
  openContractActionsId.value = null;
  openContractStatusMenuId.value =
    openContractStatusMenuId.value === contractId ? null : contractId;
};

const closeContractActionsMenu = () => {
  openContractActionsId.value = null;
  openContractStatusMenuId.value = null;
};

const contractStatusMenuItems = contract =>
  contractStatusOptions.map(option => ({
    label: option.label,
    action: 'status',
    value: option.value,
    icon: option.value === contract.status ? 'i-lucide-check' : '',
    isSelected: option.value === contract.status,
    disabled: updatingStatusContractId.value === contract.id,
  }));

const contractActionMenuItems = contract =>
  [
    {
      label: 'Contas a receber',
      action: 'receivables',
      value: 'receivables',
      icon: 'i-lucide-arrow-down-left',
    },
    {
      label: 'Contas a pagar',
      action: 'payables',
      value: 'payables',
      icon: 'i-lucide-arrow-up-right',
    },
    canEditContract(contract)
      ? {
          label: copy.editContract,
          action: 'edit',
          value: 'edit',
          icon: 'i-lucide-pencil-line',
        }
      : null,
    {
      label: copy.openLink,
      action: 'open',
      value: 'open',
      icon: 'i-lucide-external-link',
    },
    contract.status === 'pending' &&
    (contract.conversation_id || contract.contact_id)
      ? {
          label: copy.sendLink,
          action: 'send_link',
          value: 'send_link',
          icon: 'i-lucide-send',
        }
      : null,
    contract.contact_id && contract.metadata?.invoice_items?.length
      ? {
          label: copy.sendQuote,
          action: 'send_quote',
          value: 'send_quote',
          icon: 'i-lucide-file-text',
        }
      : null,
    contract.status === 'signed' && contract.receipt_url
      ? {
          label: copy.downloadReceipt,
          action: 'receipt',
          value: 'receipt',
          icon: 'i-lucide-file-down',
        }
      : null,
    contract.metadata?.document_uploads?.length
      ? {
          label: copy.viewDocuments,
          action: 'documents',
          value: 'documents',
          icon: 'i-lucide-paperclip',
        }
      : null,
    {
      label: copy.delete,
      action: 'delete',
      value: 'delete',
      icon: 'i-lucide-trash-2',
    },
  ].filter(Boolean);

const handleContractAction = async ({ action, value }, contract) => {
  closeContractActionsMenu();

  if (['receivables', 'payables'].includes(action)) {
    // Abre a tela já com o lançamento do contrato (valor restante preenchido).
    router.push({
      name: `talkvia_${action}`,
      query: { contract_id: contract.id, new: '1' },
    });
  } else if (action === 'status') {
    await updateContractStatus(contract, value);
  } else if (action === 'edit') {
    openContractEditModal(contract);
  } else if (action === 'open') {
    openContract(contract);
  } else if (action === 'send_link') {
    sendLink(contract);
  } else if (action === 'send_quote') {
    openContractQuoteDialog(contract);
  } else if (action === 'receipt') {
    window.open(contract.receipt_url, '_blank', 'noopener,noreferrer');
  } else if (action === 'documents') {
    openDocumentsModal(contract);
  } else if (action === 'delete') {
    requestRemoveContract(contract);
  }
};

const closeContractEditModal = () => {
  showContractEditModal.value = false;
  editingContract.value = null;
  contractEditInvoiceItems.value = [];
  resetContractEditInvoiceItemForm();
  closeFormSelectMenu();
};

const saveContractEdit = async () => {
  const scheduleError = contractScheduleError(contractEditForm);
  if (scheduleError) {
    useAlert(scheduleError);
    return;
  }
  if (!editingContract.value) return;
  if (!contractEditForm.talkvia_contract_template_id) {
    useAlert('Escolha o modelo do contrato.');
    return;
  }
  if (
    contractEditForm.signature_level !== 'simple' &&
    !/^(55)?\d{10,11}$/.test(
      String(contractEditForm.recipient_phone || '').replace(/\D/g, '')
    )
  ) {
    useAlert(
      'Informe o celular do cliente com DDD: o código de confirmação da assinatura é enviado pelo WhatsApp.'
    );
    return;
  }

  isSavingContractEdit.value = true;
  try {
    const payload = {
      ...contractEditForm,
      scheduled_at: contractEditForm.scheduled_at || null,
      pickup_at: contractEditForm.pickup_at || null,
      rental_days: contractEditRentalDays.value,
      invoice_items: contractEditInvoiceItems.value,
    };
    const { data } = await ContractsAPI.update(editingContract.value.id, {
      ...payload,
    });
    Object.assign(editingContract.value, data);
    closeContractEditModal();
    useAlert(copy.contractUpdated);
    await fetchContracts();
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.contractUpdateFailed);
  } finally {
    isSavingContractEdit.value = false;
  }
};

const openContractEditFromRoute = async () => {
  const contractId = Number(route.query.edit_contract_id || 0);
  if (!contractId) return;

  let contract = contracts.value.find(item => Number(item.id) === contractId);
  if (!contract) {
    try {
      const { data } = await ContractsAPI.show(contractId);
      contract = data;
    } catch {
      useAlert('Não foi possível abrir o contrato para edição.');
      return;
    }
  }

  openContractEditModal(contract);
};

// A Agenda abre "Criar contrato" de um orçamento agendado pela URL.
const convertQuoteFromRoute = async () => {
  const quoteId = route.query.convert_quote_id;
  if (!quoteId) return;
  const { convert_quote_id: _ignored, ...query } = route.query;
  router.replace({ query });
  try {
    const { data } = await QuotesAPI.show(quoteId);
    if (data.status === 'expired' || data.contract_id) {
      useAlert('Este orçamento está expirado ou já foi convertido.');
      return;
    }
    await convertQuote(data);
  } catch {
    useAlert('Não foi possível abrir este orçamento.');
  }
};

const handleContractsChanged = async () => {
  await fetchContracts();
  if (showDashboard.value) {
    await fetchAllContractsForDashboard();
  }
};

onMounted(async () => {
  // No modo assistente os dados são carregados ao abrir (openGenerator), sem lista nem filtros.
  if (props.generatorOnly) return;
  await refresh();
  await openContractEditFromRoute();
  await convertQuoteFromRoute();
  window.addEventListener('talkvia:contracts-changed', handleContractsChanged);
  document.addEventListener('pointerdown', handleDateRangeOutsidePointerDown);
});

watch(
  () => route.query.edit_contract_id,
  () => !props.generatorOnly && openContractEditFromRoute()
);
watch(
  () => route.query.convert_quote_id,
  () => !props.generatorOnly && convertQuoteFromRoute()
);

defineExpose({ openGenerator });

onBeforeUnmount(() => {
  window.removeEventListener(
    'talkvia:contracts-changed',
    handleContractsChanged
  );
  document.removeEventListener(
    'pointerdown',
    handleDateRangeOutsidePointerDown
  );
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    :class="
      generatorOnly
        ? 'hidden'
        : 'flex h-full min-h-0 flex-col bg-n-background text-n-slate-12'
    "
  >
    <header v-if="!generatorOnly" class="border-b border-n-weak px-6 py-5">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold">
            {{ copy.title }}
          </h1>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            {{ copy.description }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            v-if="activeDocumentTab === 1"
            label="Criar orçamento"
            icon="i-hugeicons-plus-sign"
            variant="faded"
            @click="quotesPanel?.openCreate()"
          />
          <button
            v-else
            type="button"
            class="h-10 bg-n-brand px-4 text-sm font-semibold text-white"
            @click="openGenerateContractModal"
          >
            <span
              class="i-lucide-file-plus-2 mr-1.5 inline-block size-4 align-middle"
            />
            {{ copy.generateContract }}
          </button>
          <button
            type="button"
            class="h-10 border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-2"
            @click="openDashboard"
          >
            <span
              class="i-lucide-bar-chart-2 mr-1.5 inline-block size-4 align-middle"
            />
            {{ copy.dashboard }}
          </button>
          <button
            type="button"
            class="h-10 border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-2"
            @click="openTemplateModal()"
          >
            <span
              class="i-lucide-file-text mr-1.5 inline-block size-4 align-middle"
            />
            {{ copy.manageTemplates }}
          </button>
          <button
            v-if="isAdmin"
            type="button"
            class="h-10 border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-2"
            @click="creditsDialogRef?.open()"
          >
            <span
              class="i-hugeicons-wallet-01 mr-1.5 inline-block size-4 align-middle"
            />
            Créditos
          </button>
        </div>
      </div>
    </header>
    <nav
      v-if="!generatorOnly"
      class="border-b border-n-weak px-6 py-3"
      aria-label="Contratos e orçamentos"
    >
      <TabBar
        :tabs="documentTabs"
        :initial-active-tab="activeDocumentTab"
        @tab-changed="changeDocumentTab"
      />
    </nav>
    <ContractCreditsDialog ref="creditsDialogRef" />

    <section class="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-6">
      <div v-if="!generatorOnly" v-show="activeDocumentTab === 1">
        <QuotesPanel ref="quotesPanel" @convert="convertQuote" />
      </div>
      <!-- Generate contract form -->
      <Teleport to="body">
        <div
          v-if="showGenerateContractModal"
          class="fixed inset-0 z-[9999] flex items-start justify-center p-4"
          :class="
            generatorOnly
              ? 'pointer-events-none'
              : 'overflow-y-auto bg-black/60 backdrop-blur-sm'
          "
          @click.self="closeGenerateContractModal"
          @keydown.esc="generatorOnly && closeGenerateContractModal()"
        >
          <section
            ref="generatorPanelRef"
            class="my-8 flex max-h-[calc(100vh-4rem)] w-full flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
            :class="
              generatorOnly ? 'pointer-events-auto max-w-4xl' : 'max-w-6xl'
            "
            :style="generatorOnly ? generatorPanelStyle : undefined"
            role="dialog"
            :aria-modal="generatorOnly ? 'false' : 'true'"
            aria-labelledby="generate-contract-title"
          >
            <header
              class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
              :class="{ 'cursor-move touch-none select-none': generatorOnly }"
              @pointerdown="generatorOnly && startGeneratorDrag($event)"
            >
              <h2 id="generate-contract-title" class="text-lg font-semibold">
                {{ copy.generateContract }}
              </h2>
              <button
                v-if="generatorOnly"
                type="button"
                class="ms-auto grid size-9 shrink-0 cursor-move place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="translate('DIALOG.MOVE_WINDOW')"
                :title="translate('DIALOG.MOVE_WINDOW')"
                @keydown="onGeneratorHandleKeydown"
              >
                <span
                  class="i-hugeicons-drag-drop-vertical size-5"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="copy.close"
                @click="closeGenerateContractModal"
              >
                <span class="i-lucide-x size-5" />
              </button>
            </header>
            <ol
              class="m-0 grid list-none grid-cols-2 gap-2 border-b border-n-weak p-4 sm:grid-cols-4"
              aria-label="Etapas do contrato"
            >
              <li v-for="(step, index) in contractSteps" :key="step.title">
                <button
                  type="button"
                  class="flex w-full items-center gap-2 rounded-lg border p-3 text-left transition disabled:cursor-not-allowed"
                  :class="
                    contractStep === index
                      ? 'border-n-brand/40 bg-n-brand/5 text-n-slate-12'
                      : 'border-transparent text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  :aria-current="contractStep === index ? 'step' : undefined"
                  :disabled="index > contractStep || isGeneratingContract"
                  @click="goToContractStep(index)"
                >
                  <span
                    class="grid size-7 shrink-0 place-content-center rounded-lg"
                    :class="
                      contractStep === index
                        ? 'bg-n-brand text-white'
                        : 'bg-n-alpha-2'
                    "
                    ><span
                      v-if="index < contractStep"
                      class="i-lucide-check size-4"
                    /><span v-else class="text-xs font-semibold">{{
                      index + 1
                    }}</span></span
                  ><span class="text-xs font-semibold">{{ step.title }}</span>
                </button>
              </li>
            </ol>
            <div
              ref="contractStepBody"
              class="min-h-0 flex-1 overflow-y-auto p-5"
            >
              <div class="mb-4 flex items-center gap-3">
                <span
                  :class="contractSteps[contractStep].icon"
                  class="size-5 shrink-0 text-n-brand"
                />
                <div>
                  <h3 class="mb-0 text-base font-semibold">
                    {{ contractSteps[contractStep].title }}
                  </h3>
                  <p class="mb-0 mt-1 text-xs text-n-slate-11">
                    {{ contractSteps[contractStep].description }}
                  </p>
                </div>
              </div>
              <fieldset
                class="m-0 min-w-0 border-0 p-0"
                :disabled="isGeneratingContract"
              >
                <div v-if="contractStep === 0" class="grid gap-4">
                  <section
                    class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                    aria-label="Contato"
                  >
                    <div
                      class="flex flex-wrap items-start justify-between gap-2"
                    >
                      <div class="min-w-0">
                        <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                          {{ copy.contactSearch }}
                        </h4>
                        <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                          Escolha o contato para preencher os dados
                          automaticamente.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        icon="i-hugeicons-user-add-01"
                        label="Cadastrar contato"
                        @click="createContactDialogRef?.dialogRef.open()"
                      />
                    </div>
                    <div
                      v-if="selectedContact"
                      class="flex items-center gap-3 rounded-lg border border-n-brand/40 bg-n-brand/5 p-3"
                    >
                      <span
                        class="grid size-10 shrink-0 place-content-center rounded-full bg-n-brand/15 text-sm font-semibold text-n-brand"
                        aria-hidden="true"
                      >
                        {{ contactInitials(selectedContact) }}
                      </span>
                      <span class="min-w-0 flex-1">
                        <strong
                          class="block truncate text-sm text-n-slate-12"
                          :title="selectedContact.name"
                        >
                          {{ selectedContact.name || 'Sem nome' }}
                        </strong>
                        <span class="block truncate text-xs text-n-slate-11">
                          {{
                            [
                              selectedContact.phone_number,
                              selectedContactDocumentLabel,
                            ]
                              .filter(Boolean)
                              .join(' · ') || 'Sem telefone ou documento'
                          }}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="faded"
                        color="slate"
                        size="sm"
                        icon="i-hugeicons-exchange-01"
                        label="Trocar"
                        @click="clearSelectedContact"
                      />
                    </div>
                    <div v-else class="relative">
                      <label class="relative block">
                        <span class="sr-only">{{ copy.contactSearch }}</span>
                        <span
                          class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                          :class="
                            isSearchingContacts
                              ? 'i-lucide-loader-2 animate-spin'
                              : 'i-hugeicons-search-01'
                          "
                        />
                        <input
                          v-model="contactSearch"
                          type="search"
                          :class="INPUT_CLASS"
                          class="!pl-9"
                          placeholder="Busque por nome, telefone ou CPF/CNPJ"
                          @input="searchContacts"
                        />
                      </label>
                      <div
                        v-if="contactResults.length"
                        class="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg"
                        role="listbox"
                      >
                        <button
                          v-for="contact in contactResults"
                          :key="contact.id"
                          type="button"
                          role="option"
                          class="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-n-alpha-2"
                          @click="selectContact(contact)"
                        >
                          <span
                            class="grid size-8 shrink-0 place-content-center rounded-full bg-n-alpha-2 text-xs font-semibold text-n-slate-11"
                            aria-hidden="true"
                          >
                            {{ contactInitials(contact) }}
                          </span>
                          <span class="min-w-0 flex-1">
                            <span
                              class="block truncate text-sm font-medium text-n-slate-12"
                            >
                              {{ contact.name || 'Sem nome' }}
                            </span>
                            <span
                              class="block truncate text-xs text-n-slate-10"
                            >
                              {{ contact.phone_number || 'Sem telefone' }}
                              <template v-if="contactCpf(contact)">
                                · CPF/CNPJ {{ contactCpf(contact) }}
                              </template>
                            </span>
                          </span>
                        </button>
                      </div>
                      <p
                        v-else-if="
                          contactSearch.trim().length >= 2 &&
                          !isSearchingContacts
                        "
                        class="mb-0 mt-1.5 text-xs text-n-slate-10"
                      >
                        Nenhum contato encontrado. Cadastre o contato ou
                        preencha os dados abaixo.
                      </p>
                    </div>
                  </section>

                  <section
                    class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                    aria-label="Contratante"
                  >
                    <div>
                      <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                        Contratante
                      </h4>
                      <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                        Dados que aparecem no contrato e no orçamento.
                      </p>
                    </div>
                    <div class="grid gap-3 lg:grid-cols-3">
                      <label :class="FIELD_LABEL_CLASS">
                        Nome
                        <input
                          v-model="contractForm.recipient_name"
                          :class="INPUT_CLASS"
                          placeholder="Nome completo ou razão social"
                        />
                      </label>
                      <div :class="FIELD_LABEL_CLASS">
                        <span>Documento</span>
                        <input
                          v-model="contractForm.recipient_cpf"
                          :class="INPUT_CLASS"
                          placeholder="CPF ou CNPJ"
                          aria-label="Documento"
                        />
                        <div
                          v-if="selectedContactDocuments.length > 1"
                          class="flex flex-wrap gap-1.5"
                          role="radiogroup"
                          aria-label="Documento do contrato"
                        >
                          <button
                            v-for="document in selectedContactDocuments"
                            :key="document.type"
                            type="button"
                            role="radio"
                            :aria-checked="
                              selectedDocumentType === document.type
                            "
                            class="rounded-md border px-2 py-0.5 text-xs font-medium transition"
                            :class="
                              selectedDocumentType === document.type
                                ? 'border-n-brand bg-n-brand/5 text-n-slate-12'
                                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                            "
                            @click="selectRecipientDocument(document.type)"
                          >
                            Usar {{ document.label }}
                          </button>
                        </div>
                      </div>
                      <label :class="FIELD_LABEL_CLASS">
                        Telefone
                        <PhoneNumberInput
                          v-model="contractForm.recipient_phone"
                          class="w-full min-w-0 [&>div:first-child]:!h-10 [&>div:first-child]:!bg-n-solid-1 [&>div:first-child>div>div:first-child>button]:!top-0 [&>div:first-child>div>div:first-child>button]:!my-auto [&>div:first-child>div>div:first-child>button]:!ml-1 [&_input]:!h-10 [&_input]:min-w-0"
                          placeholder="Digite o telefone"
                        />
                      </label>
                    </div>
                    <div :class="FIELD_LABEL_CLASS" class="lg:max-w-md">
                      <span>Modelo do contrato</span>
                      <ComboBox
                        v-model="contractForm.talkvia_contract_template_id"
                        :options="activeTemplateOptions"
                        placeholder="Selecione o modelo"
                        teleport-to-body
                      />
                      <span class="text-xs font-normal text-n-slate-10"
                        >Sem modelo, é possível enviar somente o
                        orçamento.</span
                      >
                    </div>
                  </section>

                  <section
                    class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                    :aria-label="copy.addressSection"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                          Endereço do contrato
                        </h4>
                        <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                          Confira o endereço que será utilizado no contrato.
                        </p>
                      </div>
                      <button
                        type="button"
                        class="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                        :title="
                          isAddressSectionCollapsed ? 'Expandir' : 'Minimizar'
                        "
                        :aria-expanded="!isAddressSectionCollapsed"
                        @click="
                          isAddressSectionCollapsed = !isAddressSectionCollapsed
                        "
                      >
                        <span
                          class="size-4"
                          :class="
                            isAddressSectionCollapsed
                              ? 'i-lucide-chevron-down'
                              : 'i-lucide-chevron-up'
                          "
                        />
                      </button>
                    </div>
                    <template v-if="!isAddressSectionCollapsed">
                      <div
                        class="grid grid-cols-2 gap-1 rounded-xl border border-n-weak bg-n-alpha-1 p-1 sm:max-w-md"
                        role="radiogroup"
                        aria-label="Origem do endereço"
                      >
                        <button
                          v-for="option in [
                            {
                              value: true,
                              label: 'Endereço do contato',
                              icon: 'i-hugeicons-user',
                            },
                            {
                              value: false,
                              label: 'Outro endereço',
                              icon: 'i-hugeicons-location-01',
                            },
                          ]"
                          :key="String(option.value)"
                          type="button"
                          role="radio"
                          :aria-checked="useContactAddress === option.value"
                          class="flex h-9 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-medium transition sm:text-sm"
                          :class="
                            useContactAddress === option.value
                              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                              : 'text-n-slate-11 hover:text-n-slate-12'
                          "
                          @click="setUseContactAddress(option.value)"
                        >
                          <span :class="option.icon" class="size-4" />
                          {{ option.label }}
                        </button>
                      </div>
                      <div
                        v-if="useContactAddress"
                        class="flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm"
                        :class="
                          selectedContactAddressSummary
                            ? 'border-n-weak bg-n-solid-1 text-n-slate-12'
                            : 'border-n-amber-7 bg-n-amber-2 text-n-amber-11'
                        "
                      >
                        <span
                          class="mt-0.5 size-4 shrink-0"
                          :class="
                            selectedContactAddressSummary
                              ? 'i-hugeicons-location-01 text-n-slate-10'
                              : 'i-hugeicons-alert-circle'
                          "
                        />
                        <span>
                          {{
                            selectedContactAddressSummary ||
                            (selectedContact
                              ? 'Este contato não tem endereço cadastrado. Escolha “Outro endereço” para informar o endereço do contrato.'
                              : 'Selecione um contato para usar o endereço cadastrado dele.')
                          }}
                        </span>
                      </div>
                      <div v-else class="grid gap-3 md:grid-cols-6">
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-2">
                          CEP
                          <span class="relative block">
                            <input
                              v-model="contractForm.recipient_zip_code"
                              :class="INPUT_CLASS"
                              class="!pr-10"
                              placeholder="00000-000"
                              @blur="lookupContractZipCode(true)"
                              @input="lookupContractZipCode()"
                            />
                            <button
                              type="button"
                              class="absolute right-1 top-1/2 grid size-8 -translate-y-1/2 place-content-center rounded-md text-n-brand transition hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                              :disabled="
                                cleanZipCode(contractForm.recipient_zip_code)
                                  .length !== 8 || isFetchingContractZipCode
                              "
                              :title="copy.lookupZipCode"
                              :aria-label="copy.lookupZipCode"
                              @click="lookupContractZipCode(true)"
                            >
                              <span
                                class="size-4"
                                :class="
                                  isFetchingContractZipCode
                                    ? 'i-lucide-loader-2 animate-spin'
                                    : 'i-hugeicons-search-01'
                                "
                              />
                            </button>
                          </span>
                        </label>
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-4">
                          Endereço
                          <input
                            v-model="contractForm.recipient_address"
                            :class="INPUT_CLASS"
                            placeholder="Rua, avenida ou logradouro"
                          />
                        </label>
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-2">
                          Número
                          <input
                            v-model="contractForm.recipient_address_number"
                            :class="INPUT_CLASS"
                            placeholder="Número"
                          />
                        </label>
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-2">
                          Complemento
                          <input
                            v-model="contractForm.recipient_address_complement"
                            :class="INPUT_CLASS"
                            placeholder="Apartamento, bloco, sala"
                          />
                        </label>
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-2">
                          Bairro
                          <input
                            v-model="contractForm.recipient_neighborhood"
                            :class="INPUT_CLASS"
                            placeholder="Bairro"
                          />
                        </label>
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-4">
                          Cidade
                          <input
                            v-model="contractForm.recipient_city"
                            :class="INPUT_CLASS"
                            placeholder="Cidade"
                          />
                        </label>
                        <label :class="FIELD_LABEL_CLASS" class="md:col-span-2">
                          UF
                          <input
                            v-model="contractForm.recipient_state"
                            :class="INPUT_CLASS"
                            maxlength="2"
                            placeholder="UF"
                          />
                        </label>
                      </div>
                    </template>
                  </section>
                </div>
                <div v-else-if="contractStep === 1">
                  <div
                    class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                  >
                    <h3 class="text-sm font-semibold">
                      Produtos e serviços do contrato
                    </h3>
                    <div
                      class="grid items-end gap-3"
                      :class="
                        isCustomServiceItem
                          ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem_9rem_auto]'
                          : 'lg:grid-cols-[minmax(0,1fr)_7rem_9rem_auto]'
                      "
                    >
                      <div :class="FIELD_LABEL_CLASS">
                        <span>Produto/serviço</span>
                        <ComboBox
                          :model-value="invoiceItemForm.product_value"
                          :options="productOptions"
                          placeholder="Selecione o produto ou serviço"
                          search-placeholder="Buscar produto ou serviço"
                          empty-state="Nenhum produto ou serviço encontrado"
                          teleport-to-body
                          @open="closeFormSelectMenu"
                          @update:model-value="chooseInvoiceProduct"
                        />
                      </div>
                      <label
                        v-if="isCustomServiceItem"
                        :class="FIELD_LABEL_CLASS"
                      >
                        Descrição
                        <input
                          v-model="invoiceItemForm.custom_name"
                          :class="INPUT_CLASS"
                          placeholder="Ex: Frete, comissão, montagem"
                        />
                      </label>
                      <label :class="FIELD_LABEL_CLASS">
                        Quantidade
                        <input
                          v-model="invoiceItemForm.quantity"
                          :class="INPUT_CLASS"
                          min="1"
                          type="number"
                        />
                      </label>
                      <label :class="FIELD_LABEL_CLASS">
                        Valor unitário
                        <input
                          :value="invoiceItemForm.unit_price"
                          :class="INPUT_CLASS"
                          inputmode="numeric"
                          placeholder="R$ 0,00"
                          type="text"
                          @input="maskCurrency"
                        />
                      </label>
                      <div class="mb-0 flex items-end">
                        <button
                          v-tooltip.top="copy.addProduct"
                          type="button"
                          class="flex size-10 items-center justify-center rounded-lg bg-n-brand text-white shadow-sm transition hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:bg-n-slate-4 disabled:text-n-slate-10"
                          :disabled="!canAddInvoiceItem"
                          :title="copy.addProduct"
                          @click="addInvoiceItem"
                        >
                          <span class="i-lucide-plus size-4" />
                          <span class="sr-only">{{ copy.addProduct }}</span>
                        </button>
                      </div>
                    </div>

                    <output
                      class="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm text-n-slate-11"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      <span>Prévia do item:</span>
                      <span class="tabular-nums">
                        {{ invoiceItemPreview.quantity }} ×
                        {{ formatCurrency(invoiceItemPreview.unitPrice) }} =
                      </span>
                      <strong class="tabular-nums text-n-slate-12">
                        {{ formatCurrency(invoiceItemPreview.total) }}
                      </strong>
                    </output>

                    <div v-if="invoiceItems.length" class="overflow-x-auto">
                      <table class="w-full min-w-[640px] text-left text-sm">
                        <thead
                          class="border-b border-n-weak text-xs uppercase text-n-slate-10"
                        >
                          <tr>
                            <th class="py-2">Produto/serviço</th>
                            <th class="py-2 text-right">Qtd.</th>
                            <th class="py-2 text-right">Valor unitário</th>
                            <th class="py-2 text-right">Total</th>
                            <th class="py-2 text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, index) in invoiceItems"
                            :key="`${item.product_type}-${item.product_id || item.product_name}-${index}`"
                            class="border-b border-n-weak last:border-b-0"
                          >
                            <td class="py-2 text-n-slate-12">
                              {{ item.product_name }}
                            </td>
                            <td class="px-2 py-2 text-right text-n-slate-12">
                              <input
                                :value="item.quantity"
                                class="ml-auto h-9 w-24 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-right text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                                min="1"
                                step="1"
                                type="number"
                                :aria-label="`Quantidade de ${item.product_name}`"
                                @input="
                                  updateInvoiceItemQuantity(
                                    invoiceItems,
                                    index,
                                    $event.target.value
                                  )
                                "
                              />
                            </td>
                            <td class="px-2 py-2 text-right text-n-slate-11">
                              <input
                                :value="formatCurrency(item.unit_price)"
                                class="ml-auto h-9 w-32 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-right text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                                inputmode="numeric"
                                type="text"
                                :aria-label="`Valor unitário de ${item.product_name}`"
                                @input="
                                  updateInvoiceItemUnitPrice(
                                    invoiceItems,
                                    index,
                                    $event
                                  )
                                "
                              />
                            </td>
                            <td class="py-2 pl-4 text-right text-n-slate-12">
                              {{
                                formatCurrency(
                                  rentalItemTotal(
                                    item.quantity,
                                    item.unit_price,
                                    itemRentalDays(item, contractRentalDays)
                                  )
                                )
                              }}
                              <label
                                v-if="contractRentalDays > 1"
                                class="mt-1 flex cursor-pointer items-center justify-end gap-1.5 text-xs font-normal text-n-slate-11"
                              >
                                <Checkbox
                                  :model-value="!skipsRentalDays(item)"
                                  @update:model-value="
                                    setChargePerDay(item, $event)
                                  "
                                />
                                {{ copy.chargePerDay }}
                              </label>
                            </td>
                            <td class="py-2 text-right">
                              <button
                                v-tooltip.top="copy.delete"
                                type="button"
                                class="inline-flex size-8 items-center justify-center rounded-lg text-n-ruby-11 transition hover:bg-n-ruby-3"
                                :title="copy.delete"
                                @click="removeInvoiceItem(index)"
                              >
                                <span class="i-lucide-trash-2 size-4" />
                                <span class="sr-only">{{ copy.delete }}</span>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colspan="3" class="py-2 text-right">
                              {{ copy.invoiceTotal }}
                            </th>
                            <th class="py-2 text-right">
                              {{ formatCurrency(invoiceTotal) }}
                            </th>
                            <th />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <p v-else class="mb-0 text-xs text-n-slate-10">
                      Adicione um ou mais produtos para montar o valor final do
                      contrato.
                    </p>
                  </div>
                </div>
                <div v-else-if="contractStep === 2">
                  <div
                    class="grid items-start gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]"
                  >
                    <section
                      class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                    >
                      <div class="flex items-center gap-3">
                        <span class="i-lucide-truck size-5 text-n-brand" />
                        <h3 class="mb-0 text-sm font-semibold">
                          Programação da entrega e retirada
                        </h3>
                      </div>
                      <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
                        Escolha a data e o horário combinados com o cliente.
                        Deixe em branco quando o serviço não tiver entrega ou
                        retirada.
                      </p>
                      <DateField
                        v-model="contractForm.scheduled_at"
                        label="Entrega · data e horário"
                        with-time
                      />
                      <DateField
                        v-model="contractForm.pickup_at"
                        label="Retirada · data e horário"
                        with-time
                        :min="contractForm.scheduled_at || ''"
                      />
                      <NextInput
                        :model-value="contractForm.rental_days"
                        label="Diárias"
                        inputmode="numeric"
                        class="max-w-40"
                        :message="
                          !rentalDaysTouched.create &&
                          suggestRentalDays(
                            contractForm.scheduled_at,
                            contractForm.pickup_at
                          )
                            ? 'Calculado pelas datas; ajuste se precisar.'
                            : 'Multiplica o valor de cada item.'
                        "
                        data-test="contract-rental-days"
                        @update:model-value="
                          updateRentalDays(contractForm, 'create', $event)
                        "
                      />
                      <p
                        v-if="contractScheduleError(contractForm)"
                        class="mb-0 text-xs text-n-ruby-11"
                      >
                        {{ contractScheduleError(contractForm) }}
                      </p>
                    </section>
                    <section
                      class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                    >
                      <div class="flex items-center gap-3">
                        <span class="i-lucide-wallet size-5 text-n-brand" />
                        <h3 class="mb-0 text-sm font-semibold">
                          Condições de pagamento
                        </h3>
                      </div>
                      <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
                        Escolha uma das formas de pagamento cadastradas. Os
                        vencimentos e as baixas ficam no financeiro do contrato.
                      </p>
                      <div class="space-y-2">
                        <label class="text-xs font-medium text-n-slate-11"
                          >Forma de pagamento</label
                        >
                        <ComboBox
                          v-model="contractForm.payment_method"
                          :options="paymentMethodOptions"
                          placeholder="Selecione a forma de pagamento"
                          teleport-to-body
                        />
                      </div>
                      <Button
                        label="Gerenciar formas de pagamento"
                        variant="link"
                        size="sm"
                        icon="i-lucide-settings-2"
                        @click="showAddPaymentInput = !showAddPaymentInput"
                      />
                      <!-- Inline custom payment method management -->
                      <div
                        v-if="showAddPaymentInput"
                        class="mt-2 grid gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
                      >
                        <span class="text-xs font-semibold text-n-slate-11">
                          Gerenciar formas de pagamento
                        </span>
                        <div class="flex gap-2">
                          <input
                            v-model="newPaymentMethodInput"
                            class="flex-1"
                            :class="[INPUT_CLASS]"
                            placeholder="Nova forma de pagamento"
                            @keyup.enter="saveNewPaymentMethod"
                          />
                          <button
                            type="button"
                            class="h-10 rounded-lg bg-n-brand px-3 text-sm font-semibold text-white disabled:opacity-60"
                            :disabled="
                              !newPaymentMethodInput.trim() ||
                              isSavingPaymentMethod
                            "
                            @click="saveNewPaymentMethod"
                          >
                            Salvar
                          </button>
                        </div>
                        <div
                          v-if="customPaymentMethods.length"
                          class="flex flex-wrap gap-1.5"
                        >
                          <span
                            v-for="method in customPaymentMethods"
                            :key="method"
                            class="inline-flex items-center gap-1 rounded bg-n-slate-3 px-2 py-1 text-xs"
                          >
                            {{ method }}
                            <button
                              type="button"
                              class="text-n-ruby-11 hover:text-n-ruby-9"
                              @click="removeCustomPaymentMethod(method)"
                            >
                              <span class="i-lucide-x size-3" />
                            </button>
                          </span>
                        </div>
                      </div>
                    </section>
                  </div>
                  <section
                    class="mt-4 grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                    aria-labelledby="contract-signature-title"
                  >
                    <div class="flex items-center gap-3">
                      <span class="i-hugeicons-signature size-5 text-n-brand" />
                      <div>
                        <h3
                          id="contract-signature-title"
                          class="mb-0 text-sm font-semibold"
                        >
                          Assinatura e créditos
                        </h3>
                        <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
                          Escolha como o cliente vai assinar. Quanto maior o
                          nível, mais provas de que foi o próprio cliente quem
                          assinou. Todos incluem a cláusula de aceite do meio
                          eletrônico (MP 2.200-2/2001, art. 10, § 2º).
                        </p>
                      </div>
                    </div>
                    <dl
                      v-if="creditSummary.length"
                      class="m-0 grid gap-2 sm:grid-cols-2 xl:grid-cols-4"
                      aria-label="Créditos disponíveis"
                    >
                      <div
                        v-for="item in creditSummary"
                        :key="item.key"
                        class="min-w-0 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5"
                      >
                        <dt class="text-xs text-n-slate-11">
                          {{ item.label }}
                        </dt>
                        <dd
                          class="m-0 text-base font-semibold tabular-nums text-n-slate-12"
                        >
                          {{ item.value }}
                        </dd>
                        <dd
                          v-if="item.hint"
                          class="m-0 text-xs text-n-slate-11"
                        >
                          {{ item.hint }}
                        </dd>
                      </div>
                    </dl>
                    <div
                      v-if="contractAllowance"
                      class="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 text-sm"
                      :class="
                        contractAllowance.can_generate
                          ? 'border-n-weak bg-n-solid-1 text-n-slate-11'
                          : 'border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11'
                      "
                      role="status"
                    >
                      <span class="flex min-w-0 items-start gap-2">
                        <span
                          class="i-hugeicons-contracts mt-0.5 size-4 shrink-0"
                          aria-hidden="true"
                        />
                        <span class="min-w-0">
                          {{ contractAllowanceLabel }}
                          <template v-if="!contractAllowance.can_generate">
                            .
                            {{
                              isAdmin
                                ? 'A franquia do mês acabou: compre contratos avulsos para gerar este contrato.'
                                : 'A franquia do mês acabou: peça a um administrador para comprar contratos avulsos.'
                            }}
                          </template>
                        </span>
                      </span>
                      <Button
                        v-if="
                          isAdmin &&
                          !contractAllowance.unlimited &&
                          contractAllowance.remaining_from_plan === 0
                        "
                        label="Comprar contratos avulsos"
                        size="sm"
                        variant="outline"
                        icon="i-hugeicons-shopping-cart-01"
                        @click="router.push({ name: 'talkvia_shop' })"
                      />
                    </div>
                    <SignatureLevelPicker
                      v-model="contractForm.signature_level"
                      :options="signatureLevelOptions"
                    />
                  </section>
                </div>
                <section
                  v-else
                  class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]"
                >
                  <div class="space-y-4">
                    <div
                      class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
                    >
                      <div class="mb-3 flex items-center justify-between">
                        <h3 class="mb-0 text-sm font-semibold">
                          Cliente e contrato
                        </h3>
                        <Button
                          label="Editar"
                          variant="link"
                          size="sm"
                          @click="goToContractStep(0)"
                        />
                      </div>
                      <strong
                        class="block break-words text-base text-n-slate-12"
                        >{{ contractForm.recipient_name }}</strong
                      >
                      <p class="mt-1 mb-0 text-sm text-n-slate-11">
                        {{
                          contractForm.recipient_cpf ||
                          'Documento não informado'
                        }}
                        ·
                        {{
                          contractForm.recipient_phone ||
                          'Telefone não informado'
                        }}
                      </p>
                      <p class="mt-3 mb-0 text-xs text-n-slate-11">
                        Modelo: {{ generateTemplateLabel }}
                      </p>
                      <p class="mt-2 mb-0 text-xs text-n-slate-11">
                        {{
                          [
                            contractForm.recipient_address,
                            contractForm.recipient_address_number,
                            contractForm.recipient_city,
                            contractForm.recipient_state,
                          ]
                            .filter(Boolean)
                            .join(', ') || 'Endereço não informado'
                        }}
                      </p>
                    </div>
                    <div
                      class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
                    >
                      <div class="mb-3 flex items-center justify-between">
                        <h3 class="mb-0 text-sm font-semibold">
                          Itens do contrato
                        </h3>
                        <Button
                          label="Editar"
                          variant="link"
                          size="sm"
                          @click="goToContractStep(1)"
                        />
                      </div>
                      <ul class="m-0 list-none divide-y divide-n-weak p-0">
                        <li
                          v-for="(item, index) in invoiceItems"
                          :key="index"
                          class="flex items-start justify-between gap-3 py-3"
                        >
                          <div class="min-w-0">
                            <strong
                              class="block break-words text-sm text-n-slate-12"
                              >{{ item.product_name }}</strong
                            ><span class="text-xs text-n-slate-11"
                              >{{ item.quantity }} ×
                              {{ formatCurrency(item.unit_price) }}</span
                            >
                          </div>
                          <strong
                            class="shrink-0 text-sm tabular-nums text-n-slate-12"
                            >{{
                              formatCurrency(
                                Number(item.quantity) * Number(item.unit_price)
                              )
                            }}</strong
                          >
                        </li>
                      </ul>
                    </div>
                    <div
                      class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
                    >
                      <div class="mb-3 flex items-center justify-between">
                        <h3 class="mb-0 text-sm font-semibold">Assinatura</h3>
                        <Button
                          label="Alterar"
                          variant="link"
                          size="sm"
                          @click="goToContractStep(2)"
                        />
                      </div>
                      <strong class="block text-sm text-n-slate-12">{{
                        selectedSignatureLevel.label
                      }}</strong>
                      <p class="mt-1 mb-0 text-xs text-n-slate-11">
                        {{ selectedSignatureLevel.description }}
                      </p>
                      <p
                        v-if="contractAllowanceLabel"
                        class="mt-2 mb-0 text-xs"
                        :class="
                          contractAllowance?.can_generate === false
                            ? 'text-n-ruby-11'
                            : 'text-n-slate-11'
                        "
                      >
                        {{ contractAllowanceLabel }}
                      </p>
                    </div>
                  </div>
                  <aside
                    class="self-start overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
                  >
                    <div class="border-b border-n-weak p-5">
                      <span class="text-xs text-n-slate-11"
                        >Total do contrato</span
                      ><strong
                        class="mt-2 block text-2xl tabular-nums text-n-slate-12"
                        >{{ formatCurrency(invoiceTotal) }}</strong
                      ><span class="text-xs text-n-slate-11"
                        >{{ invoiceItems.length }} item(ns)<template
                          v-if="contractRentalDays > 1"
                        >
                          · {{ contractRentalDays }} diárias</template
                        ></span
                      >
                    </div>
                    <div class="space-y-4 p-5">
                      <div>
                        <span class="block text-xs text-n-slate-11"
                          >Entrega</span
                        ><strong class="text-sm text-n-slate-12">{{
                          contractForm.scheduled_at
                            ? formatDateTime(contractForm.scheduled_at)
                            : 'Não se aplica / a combinar'
                        }}</strong>
                      </div>
                      <div>
                        <span class="block text-xs text-n-slate-11"
                          >Retirada</span
                        ><strong class="text-sm text-n-slate-12">{{
                          contractForm.pickup_at
                            ? formatDateTime(contractForm.pickup_at)
                            : 'Não se aplica / a combinar'
                        }}</strong>
                      </div>
                      <div>
                        <span class="block text-xs text-n-slate-11"
                          >Pagamento</span
                        ><strong class="text-sm text-n-slate-12">{{
                          contractForm.payment_method
                        }}</strong>
                      </div>
                      <Button
                        label="Alterar condições"
                        variant="link"
                        size="sm"
                        @click="goToContractStep(2)"
                      />
                    </div>
                  </aside>
                </section>
              </fieldset>
              <p
                v-if="contractStepMessage"
                role="alert"
                class="mb-0 mt-4 rounded-lg border border-n-ruby-5 bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
              >
                {{ contractStepMessage }}
              </p>
            </div>
            <footer
              class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak bg-n-solid-2 px-5 py-4"
            >
              <div class="flex items-center gap-3">
                <Button
                  v-if="contractStep > 0"
                  label="Voltar"
                  color="slate"
                  variant="ghost"
                  icon="i-lucide-arrow-left"
                  :disabled="isGeneratingContract"
                  @click="goToContractStep(contractStep - 1)"
                /><span class="text-xs text-n-slate-11"
                  >Etapa {{ contractStep + 1 }} de 4</span
                >
              </div>
              <Button
                v-if="contractStep < 3"
                label="Continuar"
                icon="i-lucide-arrow-right"
                trailing-icon
                @click="goToContractStep(contractStep + 1)"
              />
              <div v-else class="flex flex-wrap gap-2">
                <Button
                  type="button"
                  color="slate"
                  variant="ghost"
                  :label="copy.sendQuote"
                  :disabled="isGeneratingContract || !contractForm.contact_id"
                  @click="openQuoteDialog"
                />
                <Button
                  type="button"
                  color="slate"
                  variant="outline"
                  label="Gerar e enviar contrato"
                  :disabled="
                    isGeneratingContract ||
                    !contractForm.contact_id ||
                    !contractForm.talkvia_contract_template_id
                  "
                  @click="generateContract({ sendToContact: true })"
                />
                <Button
                  type="button"
                  :label="
                    isGeneratingContract
                      ? 'Gerando contrato…'
                      : 'Confirmar e gerar contrato'
                  "
                  :disabled="
                    isGeneratingContract ||
                    !contractForm.talkvia_contract_template_id
                  "
                  @click="generateContract"
                />
              </div>
            </footer>
          </section>
        </div>
      </Teleport>

      <!-- Stock by date panel -->
      <section
        v-if="
          !generatorOnly && activeDocumentTab === 0 && inventoryProducts.length
        "
        class="border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="mb-3 flex flex-wrap items-center gap-3">
          <h2 class="text-base font-semibold">Estoque disponível por data</h2>
          <div class="flex items-center gap-2">
            <div ref="stockDatePickerRef" class="relative">
              <button
                type="button"
                class="flex h-10 w-44 items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-left text-sm text-n-slate-12 shadow-sm transition hover:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                :class="
                  showStockDatePicker
                    ? 'border-n-brand ring-1 ring-n-brand'
                    : ''
                "
                @click="openStockDatePicker"
              >
                <span class="truncate">{{ formatDateLabel(stockDate) }}</span>
                <span class="i-lucide-calendar-days size-4 shrink-0" />
              </button>

              <div
                v-if="showStockDatePicker"
                class="absolute left-0 z-50 mt-2 w-80 rounded-xl border border-n-weak bg-n-solid-2 p-3 shadow-2xl"
              >
                <div class="mb-3 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                    @click="shiftStockDateMonth(-1)"
                  >
                    <span class="i-lucide-chevron-left size-4" />
                  </button>
                  <h3
                    class="text-center text-sm font-semibold capitalize text-n-slate-12"
                  >
                    {{ monthTitle(stockCalendarMonth.month) }}
                  </h3>
                  <button
                    type="button"
                    class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                    @click="shiftStockDateMonth(1)"
                  >
                    <span class="i-lucide-chevron-right size-4" />
                  </button>
                </div>

                <div
                  class="mb-2 grid grid-cols-7 text-center text-xs font-semibold text-n-slate-10"
                >
                  <span
                    v-for="(dayName, dayIndex) in weekDays"
                    :key="`stock-${dayIndex}`"
                    class="py-1"
                  >
                    {{ dayName }}
                  </span>
                </div>
                <div class="grid grid-cols-7 text-center text-sm">
                  <button
                    v-for="day in stockCalendarMonth.days"
                    :key="day.value"
                    type="button"
                    class="flex h-9 items-center justify-center transition"
                    :class="stockCalendarDayClass(day)"
                    @click="selectStockDate(day.value)"
                  >
                    {{ day.label }}
                  </button>
                </div>
              </div>
            </div>
            <Button
              type="button"
              label="Consultar"
              :is-loading="isLoadingStock"
              :disabled="isLoadingStock"
              @click="fetchStockByDate"
            />
          </div>
        </div>
        <div
          v-if="stockByDateResults.length"
          class="relative overflow-x-auto rounded-lg border border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak bg-n-alpha-1">
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                >
                  Produto
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                >
                  Em estoque
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                >
                  Reservado
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                >
                  Disponível
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in stockByDateResults"
                :key="row.product_id"
                class="border-b border-n-weak last:border-0"
                :class="
                  Number(row.available) <= 0 ? 'bg-n-ruby-3 text-n-ruby-11' : ''
                "
              >
                <td class="px-3 py-2 font-medium text-n-slate-12">
                  {{ row.product_name }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ Number(row.quantity_on_hand).toLocaleString('pt-BR') }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ Number(row.reserved).toLocaleString('pt-BR') }}
                </td>
                <td
                  class="px-3 py-2 text-right font-semibold"
                  :class="
                    Number(row.available) <= 0
                      ? 'text-n-ruby-11'
                      : 'text-n-green-11'
                  "
                >
                  {{ Number(row.available).toLocaleString('pt-BR') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="mb-0 text-xs text-n-slate-10">
          Clique em "Consultar" para ver a disponibilidade dos produtos na data
          selecionada.
        </p>
      </section>

      <!-- Contracts table -->
      <section
        v-if="!generatorOnly"
        v-show="activeDocumentTab === 0"
        class="border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="mb-4 space-y-4">
          <div>
            <h2 class="text-base font-semibold">
              {{ copy.contracts }}
            </h2>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ summary.pending || 0 }} pendentes ·
              {{ summary.signed || 0 }} assinados ·
              {{ summary.awaiting_delivery || 0 }} ag. entrega ·
              {{ summary.completed || 0 }} concluídos ·
              {{ summary.expired || 0 }} expirados
            </p>
          </div>
          <div
            class="grid w-full items-end gap-3 md:grid-cols-2 xl:grid-cols-[10rem_minmax(15rem,1.4fr)_minmax(12rem,1fr)_minmax(12rem,1fr)]"
          >
            <div :class="FIELD_LABEL_CLASS">
              <span>Status</span>
              <div
                v-on-clickaway="() => (showContractStatusFilter = false)"
                class="relative"
              >
                <Button
                  type="button"
                  color="slate"
                  variant="faded"
                  size="md"
                  trailing-icon
                  icon="i-lucide-chevron-down"
                  class="!h-10 w-full !justify-between !rounded-lg !border !border-n-weak !bg-n-solid-1 !px-3 !text-n-slate-12 shadow-sm"
                  :class="
                    showContractStatusFilter
                      ? '!border-n-brand !bg-n-alpha-2 ring-1 ring-n-brand'
                      : ''
                  "
                  :label="contractStatusFilterLabel"
                  @click="showContractStatusFilter = !showContractStatusFilter"
                />
                <DropdownMenu
                  v-if="showContractStatusFilter"
                  :menu-items="contractStatusFilterItems"
                  class="left-0 top-11 w-full text-left"
                  @action="applyContractStatusFilter"
                />
              </div>
            </div>
            <div :class="FIELD_LABEL_CLASS">
              <label for="contract-search" class="mb-0">Buscar</label>
              <div class="flex min-w-0 gap-2">
                <input
                  id="contract-search"
                  v-model="filters.q"
                  :class="INPUT_CLASS"
                  :placeholder="copy.searchPlaceholder"
                  @keyup.enter="fetchContracts"
                />
                <Button
                  type="button"
                  label="Buscar"
                  icon="i-lucide-search"
                  class="shrink-0"
                  @click="fetchContracts"
                />
              </div>
            </div>
            <div
              v-for="filter in dateRangeFilters"
              :key="filter.key"
              :ref="element => setDateRangeFilterRef(filter.key, element)"
              class="relative"
            >
              <span class="mb-1.5 block text-sm font-medium text-n-slate-12">
                {{ filter.label }}
              </span>
              <button
                type="button"
                class="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-left text-sm text-n-slate-12 shadow-sm transition hover:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                @click="openDateRangeFilter(filter)"
              >
                <span class="flex min-w-0 items-center gap-2">
                  <span class="i-lucide-calendar-days size-4 shrink-0" />
                  <span class="truncate">
                    {{ dateRangeButtonLabel(filter) }}
                  </span>
                </span>
                <span class="i-lucide-chevron-down size-4 shrink-0" />
              </button>

              <div
                v-if="activeDateRangeFilter === filter.key"
                class="absolute right-0 z-50 mt-2 w-[min(56rem,calc(100vw-3rem))] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
              >
                <div class="grid lg:grid-cols-[14rem_minmax(0,1fr)]">
                  <aside
                    class="border-b border-n-weak bg-n-solid-1 py-2 lg:border-b-0 lg:border-r"
                  >
                    <div
                      class="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                    >
                      Intervalo de data
                    </div>
                    <button
                      v-for="preset in dateRangePresets"
                      :key="preset.key"
                      type="button"
                      class="flex w-full items-center px-4 py-3 text-left text-sm font-medium transition hover:bg-n-alpha-2"
                      :class="
                        dateRangeDraft.preset === preset.key
                          ? 'bg-n-alpha-3 text-n-slate-12'
                          : 'text-n-slate-11'
                      "
                      @click="setDateRangePreset(preset.key)"
                    >
                      {{ preset.label }}
                    </button>
                  </aside>

                  <div class="grid gap-4 p-4">
                    <div class="grid gap-3 md:grid-cols-2">
                      <DateField
                        v-model="dateRangeDraft.start"
                        label="Data de início"
                        :max="dateRangeDraft.end || ''"
                        :clearable="false"
                      />
                      <DateField
                        v-model="dateRangeDraft.end"
                        label="Data final"
                        :min="dateRangeDraft.start || ''"
                        :clearable="false"
                      />
                    </div>

                    <div class="grid gap-4 lg:grid-cols-2">
                      <section
                        v-for="(month, monthIndex) in calendarMonths"
                        :key="month.month.toISOString()"
                        class="min-w-0"
                      >
                        <div
                          class="mb-3 flex items-center justify-between gap-3"
                        >
                          <button
                            v-if="monthIndex === 0"
                            type="button"
                            class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                            @click="shiftCalendarMonth(-1)"
                          >
                            <span class="i-lucide-chevron-left size-4" />
                          </button>
                          <span v-else class="size-8" />
                          <h3
                            class="text-center text-sm font-semibold capitalize text-n-slate-12"
                          >
                            {{ monthTitle(month.month) }}
                          </h3>
                          <button
                            v-if="monthIndex === 1"
                            type="button"
                            class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                            @click="shiftCalendarMonth(1)"
                          >
                            <span class="i-lucide-chevron-right size-4" />
                          </button>
                          <span v-else class="size-8" />
                        </div>

                        <div
                          class="mb-2 grid grid-cols-7 text-center text-xs font-semibold text-n-slate-10"
                        >
                          <span
                            v-for="(dayName, dayIndex) in weekDays"
                            :key="`${month.month.toISOString()}-${dayIndex}`"
                            class="py-1"
                          >
                            {{ dayName }}
                          </span>
                        </div>
                        <div class="grid grid-cols-7 text-center text-sm">
                          <button
                            v-for="day in month.days"
                            :key="day.value"
                            type="button"
                            class="flex h-9 items-center justify-center transition"
                            :class="calendarDayClass(day)"
                            @click="selectDateRangeDay(day.value)"
                          >
                            {{ day.label }}
                          </button>
                        </div>
                      </section>
                    </div>

                    <footer
                      class="flex items-center justify-between border-t border-n-weak pt-4"
                    >
                      <Button
                        type="button"
                        label="Limpar"
                        color="slate"
                        variant="ghost"
                        @click="clearDateRangeFilter(filter)"
                      />
                      <Button
                        type="button"
                        label="Aplicar"
                        @click="applyDateRangeFilter(filter)"
                      />
                    </footer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="overflow-visible">
          <table class="w-full table-fixed text-left text-sm">
            <thead
              class="border-b border-n-weak text-xs uppercase text-n-slate-10"
            >
              <tr>
                <th class="w-[26%] py-2 pr-3">Cliente</th>
                <th class="w-[16%] py-2 pr-3">Modelo</th>
                <th class="w-[14%] py-2 pr-3 text-right">Valor / Pgto.</th>
                <th class="hidden w-[18%] py-2 pr-3 xl:table-cell">
                  Entrega / Retirada
                </th>
                <th class="w-[18%] py-2 pr-3">Status</th>
                <th class="w-12 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contract in contracts"
                :key="contract.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-3 pr-3 align-top">
                  <div class="grid min-w-0 gap-1">
                    <span class="truncate font-medium text-n-slate-12">
                      {{ contract.recipient_name || '-' }}
                    </span>
                    <span class="truncate text-xs text-n-slate-10">
                      {{ contract.recipient_cpf || '-' }} ·
                      {{ contract.recipient_phone || '-' }}
                    </span>
                    <button
                      v-if="contract.conversation_id"
                      type="button"
                      class="inline-flex w-fit items-center gap-1 text-xs font-medium text-n-brand transition hover:text-n-brand/80"
                      @click="openConversation(contract)"
                    >
                      <span class="i-lucide-message-circle size-3.5" />
                      {{ copy.openConversation }}
                    </button>
                  </div>
                </td>
                <td class="py-3 pr-3 align-top">
                  <span class="line-clamp-2">
                    {{ contract.template_name || '-' }}
                  </span>
                </td>
                <td class="py-3 pr-3 text-right align-top">
                  <div class="grid min-w-0 gap-1">
                    <span class="font-medium text-n-slate-12">
                      {{
                        contract.metadata?.invoice_total != null
                          ? formatCurrency(contract.metadata.invoice_total)
                          : '-'
                      }}
                    </span>
                    <span class="truncate text-xs text-n-slate-10">
                      {{ contract.payment_method || '-' }}
                    </span>
                  </div>
                </td>
                <td class="hidden py-3 pr-3 align-top xl:table-cell">
                  <div class="grid gap-1 text-xs text-n-slate-11">
                    <span>
                      <span class="font-medium text-n-slate-12">Entrega:</span>
                      {{ formatDateTime(contract.scheduled_at) }}
                    </span>
                    <span>
                      <span class="font-medium text-n-slate-12">Retirada:</span>
                      {{ formatDateTime(contract.pickup_at) }}
                    </span>
                  </div>
                </td>
                <td class="py-3 pr-3 align-top">
                  <div class="grid gap-1.5">
                    <span
                      class="inline-flex w-fit items-center rounded-md border px-2 py-1 text-xs font-semibold"
                      :class="statusBadgeClass(contract.status)"
                    >
                      {{ contractStatusLabel(contract.status) }}
                    </span>
                    <ContractStockPending
                      :contract="contract"
                      @updated="Object.assign(contract, $event)"
                    />
                    <span class="break-words text-xs text-n-slate-10">
                      <span class="block"
                        >A receber:
                        {{
                          financialLabel(
                            contract.financial_summary?.receivable?.status
                          )
                        }}</span
                      >
                      <span class="block"
                        >A pagar:
                        {{
                          financialLabel(
                            contract.financial_summary?.payable?.status
                          )
                        }}</span
                      >
                      Validade: {{ contract.expires_at || '-' }}
                    </span>
                    <span class="text-xs text-n-slate-10 xl:hidden">
                      Entrega: {{ formatDateTime(contract.scheduled_at) }}
                    </span>
                    <span class="text-xs text-n-slate-10 xl:hidden">
                      Retirada: {{ formatDateTime(contract.pickup_at) }}
                    </span>
                  </div>
                </td>
                <td class="py-3 text-right align-top">
                  <div
                    v-on-clickaway="closeContractActionsMenu"
                    class="relative inline-flex justify-end gap-1.5"
                  >
                    <div class="relative">
                      <Button
                        v-tooltip.top="'Alterar status'"
                        type="button"
                        color="slate"
                        variant="ghost"
                        size="sm"
                        icon="i-lucide-list-checks"
                        :is-loading="updatingStatusContractId === contract.id"
                        :class="
                          openContractStatusMenuId === contract.id
                            ? 'bg-n-alpha-2'
                            : ''
                        "
                        @click.stop="toggleContractStatusMenu(contract.id)"
                      />
                      <DropdownMenu
                        v-if="openContractStatusMenuId === contract.id"
                        :menu-items="contractStatusMenuItems(contract)"
                        class="right-0 top-9 w-52 text-left"
                        @click.stop
                        @action="handleContractAction($event, contract)"
                      />
                    </div>
                    <div class="relative">
                      <Button
                        v-tooltip.top="'Ações'"
                        type="button"
                        icon="i-lucide-ellipsis-vertical"
                        color="slate"
                        variant="ghost"
                        size="sm"
                        :class="
                          openContractActionsId === contract.id
                            ? 'bg-n-alpha-2'
                            : ''
                        "
                        @click.stop="toggleContractActionsMenu(contract.id)"
                      />
                      <DropdownMenu
                        v-if="openContractActionsId === contract.id"
                        :menu-items="contractActionMenuItems(contract)"
                        class="right-0 top-9 w-56 text-left"
                        @click.stop
                        @action="handleContractAction($event, contract)"
                      />
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-if="!contracts.length && !isLoading">
                <td colspan="6" class="py-8 text-center text-n-slate-10">
                  Nenhum contrato encontrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>

    <ContractSendDialog
      ref="contractSendDialogRef"
      :quote-templates="quoteTemplates"
      :is-sending="isSendingDocument"
      @send="handleSendContractLink"
      @send-quote="handleSendQuote"
      @generate-quote-pdf="handleGenerateQuotePdf"
      @generate-receipt="handleGenerateReceipt"
    />
    <Dialog
      ref="deleteContractDialogRef"
      type="alert"
      width="md"
      :title="copy.deleteTitle"
      :description="copy.deleteDescription"
      :cancel-button-label="copy.cancel"
      :confirm-button-label="copy.confirmDelete"
      :is-loading="isDeletingContract"
      @close="closeDeleteContractDialog"
      @confirm="confirmRemoveContract"
    >
      <p v-if="contractPendingDelete" class="mb-0 text-sm text-n-slate-11">
        {{ contractPendingDelete.recipient_name }} ·
        {{ contractPendingDelete.template_name }}
      </p>
    </Dialog>

    <Dialog
      ref="reopenDialogRef"
      width="md"
      :title="copy.reopenTitle"
      :description="copy.reopenDescription"
      :cancel-button-label="copy.cancel"
      :confirm-button-label="copy.reopenConfirm"
      @close="closeReopenDialog"
      @confirm="confirmReopenContract"
    >
      <TextArea
        v-model="reopenReason"
        :label="copy.reopenReasonLabel"
        :placeholder="copy.reopenReasonPlaceholder"
        :max-length="250"
      />
    </Dialog>

    <!-- Contract edit modal -->
    <Teleport to="body">
      <div
        v-if="showContractEditModal"
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
        @click.self="closeContractEditModal"
      >
        <section
          class="flex max-h-[calc(100dvh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contract-edit-title"
        >
          <header
            class="flex items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
          >
            <div class="flex min-w-0 items-start gap-3">
              <span
                class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-brand/10 text-n-brand"
                aria-hidden="true"
              >
                <span class="i-hugeicons-file-edit size-5" />
              </span>
              <div class="min-w-0">
                <h2
                  id="contract-edit-title"
                  class="m-0 truncate text-lg font-semibold text-n-slate-12"
                >
                  {{ copy.editContractTitle }}
                </h2>
                <p
                  v-if="editingContract"
                  class="mb-0 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-11"
                >
                  <span class="truncate">{{
                    editingContract.recipient_name
                  }}</span>
                  <span
                    class="rounded-full bg-n-alpha-2 px-2 py-0.5 font-medium text-n-slate-11"
                  >
                    {{ contractStatusLabel(editingContract.status) }}
                  </span>
                </p>
              </div>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.close"
              @click="closeContractEditModal"
            >
              <span class="i-hugeicons-cancel-01 size-5" aria-hidden="true" />
            </button>
          </header>

          <form
            class="flex min-h-0 flex-1 flex-col"
            @submit.prevent="saveContractEdit"
          >
            <div class="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 sm:p-5">
              <div class="grid gap-4 lg:grid-cols-2">
                <section
                  class="grid content-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                  aria-labelledby="contract-edit-client"
                >
                  <h3
                    id="contract-edit-client"
                    class="m-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
                  >
                    <span
                      class="i-hugeicons-user size-4 text-n-brand"
                      aria-hidden="true"
                    />
                    Cliente
                  </h3>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Nome do cliente
                    <input
                      v-model="contractEditForm.recipient_name"
                      :class="EDIT_INPUT_CLASS"
                      autocomplete="name"
                      required
                    />
                  </label>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label :class="EDIT_FIELD_LABEL_CLASS">
                      CPF/CNPJ
                      <input
                        v-model="contractEditForm.recipient_cpf"
                        :class="EDIT_INPUT_CLASS"
                        autocomplete="off"
                      />
                    </label>
                    <label :class="EDIT_FIELD_LABEL_CLASS">
                      Celular
                      <input
                        v-model="contractEditForm.recipient_phone"
                        :class="EDIT_INPUT_CLASS"
                        autocomplete="tel"
                      />
                    </label>
                  </div>
                </section>

                <section
                  class="grid content-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                  aria-labelledby="contract-edit-delivery"
                >
                  <h3
                    id="contract-edit-delivery"
                    class="m-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
                  >
                    <span
                      class="i-hugeicons-delivery-truck-01 size-4 text-n-brand"
                      aria-hidden="true"
                    />
                    Entrega e pagamento
                  </h3>
                  <div :class="EDIT_FIELD_LABEL_CLASS">
                    <span>Forma de pagamento</span>
                    <ComboBox
                      v-model="contractEditForm.payment_method"
                      :options="editPaymentMethodOptions"
                      placeholder="Selecione"
                      teleport-to-body
                    />
                  </div>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <DateField
                      v-model="contractEditForm.scheduled_at"
                      label="Entrega · data e horário"
                      with-time
                    />
                    <DateField
                      v-model="contractEditForm.pickup_at"
                      label="Retirada · data e horário"
                      with-time
                      :min="contractEditForm.scheduled_at || ''"
                    />
                    <NextInput
                      :model-value="contractEditForm.rental_days"
                      label="Diárias"
                      inputmode="numeric"
                      message="Multiplica o valor de cada item."
                      data-test="contract-edit-rental-days"
                      @update:model-value="
                        updateRentalDays(contractEditForm, 'edit', $event)
                      "
                    />
                  </div>
                  <p
                    v-if="contractScheduleError(contractEditForm)"
                    class="mb-0 text-xs text-n-ruby-11"
                  >
                    {{ contractScheduleError(contractEditForm) }}
                  </p>
                </section>
              </div>

              <section
                class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                aria-labelledby="contract-edit-signature"
              >
                <div>
                  <h3
                    id="contract-edit-signature"
                    class="m-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
                  >
                    <span
                      class="i-hugeicons-signature size-4 text-n-brand"
                      aria-hidden="true"
                    />
                    Modelo e assinatura
                  </h3>
                  <p class="mb-0 mt-1 text-xs text-n-slate-11">
                    Define o texto que o cliente lê e como ele confirma a
                    identidade ao assinar.
                  </p>
                </div>
                <div
                  class="grid items-start gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
                >
                  <div :class="EDIT_FIELD_LABEL_CLASS">
                    <span>Modelo do contrato</span>
                    <ComboBox
                      v-model="contractEditForm.talkvia_contract_template_id"
                      :options="contractEditTemplateOptions"
                      placeholder="Selecione o modelo"
                      teleport-to-body
                    />
                  </div>
                  <div :class="EDIT_FIELD_LABEL_CLASS">
                    <span>Documentos pedidos ao cliente</span>
                    <div
                      class="flex min-h-9 flex-wrap items-center gap-1.5 text-xs font-normal"
                    >
                      <span
                        v-for="document in contractEditRequiredDocuments"
                        :key="document"
                        class="inline-flex items-center gap-1 rounded-full border border-n-weak bg-n-solid-2 px-2 py-0.5 text-n-slate-12"
                      >
                        <span
                          class="i-hugeicons-file-attachment size-3.5 text-n-slate-11"
                          aria-hidden="true"
                        />
                        {{ document }}
                      </span>
                      <span
                        v-if="!contractEditRequiredDocuments.length"
                        class="text-n-slate-10"
                      >
                        Este modelo não pede anexos.
                      </span>
                    </div>
                  </div>
                </div>
                <SignatureLevelPicker
                  v-model="contractEditForm.signature_level"
                  :options="contractEditSignatureLevelOptions"
                />
                <p
                  v-if="contractEditChangesSignature"
                  class="mb-0 flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs leading-relaxed text-n-amber-12"
                  role="status"
                >
                  <span
                    class="i-hugeicons-alert-02 mt-0.5 size-4 shrink-0 text-n-amber-11"
                    aria-hidden="true"
                  />
                  O documento será atualizado com o novo modelo ou nível. Se o
                  link já foi enviado, o cliente verá a nova versão ao abrir e
                  precisará assinar por ela.
                </p>
              </section>

              <section
                class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                aria-labelledby="contract-edit-address"
              >
                <h3
                  id="contract-edit-address"
                  class="m-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
                >
                  <span
                    class="i-hugeicons-location-01 size-4 text-n-brand"
                    aria-hidden="true"
                  />
                  Endereço do contrato
                </h3>
                <div class="grid gap-3 md:grid-cols-[0.75fr_1.9fr_0.6fr]">
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    CEP
                    <div class="flex gap-2">
                      <input
                        v-model="contractEditForm.recipient_zip_code"
                        :class="EDIT_INPUT_CLASS"
                        autocomplete="postal-code"
                        placeholder="00000-000"
                        @blur="lookupContractEditZipCode(true)"
                        @input="lookupContractEditZipCode()"
                      />
                      <button
                        type="button"
                        class="flex h-9 shrink-0 items-center justify-center rounded-md border border-n-weak bg-n-solid-2 px-3 text-n-brand shadow-sm transition hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="
                          cleanZipCode(contractEditForm.recipient_zip_code)
                            .length !== 8 || isFetchingContractEditZipCode
                        "
                        :title="copy.lookupZipCode"
                        :aria-label="copy.lookupZipCode"
                        @click="lookupContractEditZipCode(true)"
                      >
                        <span
                          class="size-4"
                          :class="
                            isFetchingContractEditZipCode
                              ? 'i-hugeicons-loading-03 animate-spin'
                              : 'i-hugeicons-search-01'
                          "
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Endereço
                    <input
                      v-model="contractEditForm.recipient_address"
                      :class="EDIT_INPUT_CLASS"
                      autocomplete="street-address"
                    />
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Número
                    <input
                      v-model="contractEditForm.recipient_address_number"
                      :class="EDIT_INPUT_CLASS"
                    />
                  </label>
                </div>
                <div class="grid gap-3 md:grid-cols-[1.2fr_1fr_1fr_0.4fr]">
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Complemento
                    <input
                      v-model="contractEditForm.recipient_address_complement"
                      :class="EDIT_INPUT_CLASS"
                    />
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Bairro
                    <input
                      v-model="contractEditForm.recipient_neighborhood"
                      :class="EDIT_INPUT_CLASS"
                    />
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Cidade
                    <input
                      v-model="contractEditForm.recipient_city"
                      :class="EDIT_INPUT_CLASS"
                    />
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    UF
                    <input
                      v-model="contractEditForm.recipient_state"
                      :class="EDIT_INPUT_CLASS"
                      maxlength="2"
                    />
                  </label>
                </div>
              </section>

              <section
                class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
                aria-labelledby="contract-edit-items"
              >
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <h3
                    id="contract-edit-items"
                    class="m-0 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
                  >
                    <span
                      class="i-hugeicons-package size-4 text-n-brand"
                      aria-hidden="true"
                    />
                    Produtos e serviços
                  </h3>
                  <span
                    v-if="contractEditInvoiceItems.length"
                    class="text-xs text-n-slate-11"
                  >
                    {{ contractEditInvoiceItems.length }}
                    {{
                      contractEditInvoiceItems.length === 1 ? 'item' : 'itens'
                    }}
                  </span>
                </div>
                <div
                  class="grid items-end gap-2.5 rounded-lg border border-dashed border-n-weak p-3"
                  :class="
                    isContractEditCustomServiceItem
                      ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem_9rem_auto]'
                      : 'lg:grid-cols-[minmax(0,1fr)_7rem_9rem_auto]'
                  "
                >
                  <div :class="EDIT_FIELD_LABEL_CLASS">
                    <span>Produto/serviço</span>
                    <ComboBox
                      :model-value="contractEditInvoiceItemForm.product_value"
                      :options="contractEditProductOptions"
                      placeholder="Selecione o produto ou serviço"
                      search-placeholder="Buscar produto ou serviço"
                      empty-state="Nenhum produto ou serviço encontrado"
                      teleport-to-body
                      @open="closeFormSelectMenu"
                      @update:model-value="chooseContractEditInvoiceProduct"
                    />
                  </div>
                  <label
                    v-if="isContractEditCustomServiceItem"
                    :class="EDIT_FIELD_LABEL_CLASS"
                  >
                    Descrição
                    <input
                      v-model="contractEditInvoiceItemForm.custom_name"
                      :class="EDIT_INPUT_CLASS"
                      placeholder="Ex: Frete, comissão, montagem"
                    />
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Quantidade
                    <input
                      v-model="contractEditInvoiceItemForm.quantity"
                      :class="EDIT_INPUT_CLASS"
                      min="1"
                      type="number"
                    />
                  </label>
                  <label :class="EDIT_FIELD_LABEL_CLASS">
                    Valor unitário
                    <input
                      :value="contractEditInvoiceItemForm.unit_price"
                      :class="EDIT_INPUT_CLASS"
                      inputmode="numeric"
                      placeholder="R$ 0,00"
                      type="text"
                      @input="maskContractEditCurrency"
                    />
                  </label>
                  <div class="mb-0 flex items-end">
                    <button
                      v-tooltip.top="copy.addProduct"
                      type="button"
                      class="relative flex size-9 items-center justify-center rounded-md bg-n-brand text-white shadow-sm transition hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:bg-n-slate-4 disabled:text-n-slate-10"
                      :disabled="!canAddContractEditInvoiceItem"
                      :title="copy.addProduct"
                      @click="addContractEditInvoiceItem"
                    >
                      <span
                        class="i-hugeicons-add-01 size-4"
                        aria-hidden="true"
                      />
                      <span class="sr-only">{{ copy.addProduct }}</span>
                    </button>
                  </div>
                </div>

                <div
                  v-if="contractEditInvoiceItems.length"
                  class="relative overflow-x-auto rounded-lg border border-n-weak"
                >
                  <table class="w-full min-w-[640px] text-left text-xs">
                    <thead
                      class="bg-n-alpha-1 text-[11px] uppercase text-n-slate-10"
                    >
                      <tr>
                        <th class="px-3 py-2">Produto/serviço</th>
                        <th class="px-2 py-2 text-right">Qtd.</th>
                        <th class="px-2 py-2 text-right">Valor unitário</th>
                        <th class="px-2 py-2 text-right">Total</th>
                        <th class="px-3 py-2 text-right">
                          <span class="sr-only">Ações</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in contractEditInvoiceItems"
                        :key="`${item.product_type}-${item.product_id || item.product_name}-${index}`"
                        class="border-t border-n-weak"
                      >
                        <td class="px-3 py-1.5 text-n-slate-12">
                          {{ item.product_name }}
                        </td>
                        <td class="px-2 py-1.5 text-right text-n-slate-12">
                          <input
                            :value="item.quantity"
                            class="ml-auto h-8 w-20 rounded-md border border-n-weak bg-n-solid-1 px-2 text-right text-xs text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                            min="1"
                            step="1"
                            type="number"
                            :aria-label="`Quantidade de ${item.product_name}`"
                            @input="
                              updateInvoiceItemQuantity(
                                contractEditInvoiceItems,
                                index,
                                $event.target.value
                              )
                            "
                          />
                        </td>
                        <td class="px-2 py-1.5 text-right text-n-slate-11">
                          <input
                            :value="formatCurrency(item.unit_price)"
                            class="ml-auto h-8 w-28 rounded-md border border-n-weak bg-n-solid-1 px-2 text-right text-xs text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                            inputmode="numeric"
                            type="text"
                            :aria-label="`Valor unitário de ${item.product_name}`"
                            @input="
                              updateInvoiceItemUnitPrice(
                                contractEditInvoiceItems,
                                index,
                                $event
                              )
                            "
                          />
                        </td>
                        <td
                          class="px-2 py-1.5 text-right font-medium text-n-slate-12"
                        >
                          {{
                            formatCurrency(
                              rentalItemTotal(
                                item.quantity,
                                item.unit_price,
                                itemRentalDays(item, contractEditRentalDays)
                              )
                            )
                          }}
                          <label
                            v-if="contractEditRentalDays > 1"
                            class="mt-1 flex cursor-pointer items-center justify-end gap-1.5 text-xs font-normal text-n-slate-11"
                          >
                            <Checkbox
                              :model-value="!skipsRentalDays(item)"
                              @update:model-value="
                                setChargePerDay(item, $event)
                              "
                            />
                            {{ copy.chargePerDay }}
                          </label>
                        </td>
                        <td class="px-3 py-1.5 text-right">
                          <button
                            v-tooltip.top="copy.delete"
                            type="button"
                            class="relative inline-flex size-8 items-center justify-center rounded-lg text-n-ruby-11 transition hover:bg-n-ruby-3"
                            :title="copy.delete"
                            @click="removeContractEditInvoiceItem(index)"
                          >
                            <span
                              class="i-hugeicons-delete-02 size-4"
                              aria-hidden="true"
                            />
                            <span class="sr-only">{{ copy.delete }}</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="mb-0 text-xs text-n-slate-10">
                  Adicione um ou mais produtos para montar o valor final do
                  contrato.
                </p>
              </section>
            </div>

            <footer
              class="flex flex-col-reverse gap-3 border-t border-n-weak bg-n-solid-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <p class="m-0 text-sm text-n-slate-11">
                {{ copy.invoiceTotal }}:
                <strong class="text-base text-n-slate-12">{{
                  formatCurrency(contractEditInvoiceTotal)
                }}</strong>
              </p>
              <div class="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="faded"
                  color="slate"
                  :label="copy.cancel"
                  :disabled="isSavingContractEdit"
                  @click="closeContractEditModal"
                />
                <Button
                  type="submit"
                  icon="i-hugeicons-checkmark-circle-02"
                  :label="copy.saveContract"
                  :is-loading="isSavingContractEdit"
                  :disabled="isSavingContractEdit"
                />
              </div>
            </footer>
          </form>
        </section>
      </div>
    </Teleport>

    <!-- Template modal -->
    <Teleport to="body">
      <div
        v-if="showTemplateModal"
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/55 p-4 backdrop-blur-sm"
        @click.self="showTemplateModal = false"
      >
        <section
          class="my-6 flex max-h-[calc(100vh-3rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contract-templates-title"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-4"
          >
            <div class="flex min-w-0 items-start gap-3">
              <span
                class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
              >
                <span class="i-hugeicons-legal-document-01 size-5" />
              </span>
              <div class="min-w-0">
                <h2
                  id="contract-templates-title"
                  class="m-0 text-lg font-semibold text-n-slate-12"
                >
                  {{ copy.manageTemplates }}
                </h2>
                <p class="mb-0 mt-0.5 text-sm text-n-slate-11">
                  Monte contratos e orçamentos com os dados do cliente e dos
                  produtos preenchidos automaticamente.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.close"
              @click="showTemplateModal = false"
            >
              <span class="i-hugeicons-cancel-01 size-5" />
            </button>
          </header>

          <div class="grid min-h-0 flex-1 lg:grid-cols-[300px_minmax(0,1fr)]">
            <!-- Template list -->
            <aside
              class="flex min-h-0 flex-col gap-3 border-b border-n-weak bg-n-solid-2 p-4 lg:border-b-0 lg:border-r"
              aria-label="Modelos criados"
            >
              <div
                class="grid grid-cols-2 gap-1 rounded-xl border border-n-weak bg-n-alpha-1 p-1"
                role="tablist"
              >
                <button
                  v-for="kindOption in [
                    {
                      value: 'contract',
                      label: copy.contractTemplates,
                      icon: 'i-hugeicons-legal-document-01',
                    },
                    {
                      value: 'quote',
                      label: copy.quoteTemplates,
                      icon: 'i-hugeicons-file-02',
                    },
                  ]"
                  :key="kindOption.value"
                  type="button"
                  role="tab"
                  :aria-selected="templateModalKind === kindOption.value"
                  class="flex h-9 items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-medium transition"
                  :class="
                    templateModalKind === kindOption.value
                      ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                      : 'text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="
                    templateModalKind = kindOption.value;
                    resetTemplateForm(kindOption.value);
                  "
                >
                  <span :class="kindOption.icon" class="size-4" />
                  {{ kindOption.label }}
                </button>
              </div>
              <Button
                :label="copy.newTemplate"
                icon="i-hugeicons-add-01"
                variant="faded"
                class="w-full"
                @click="resetTemplateForm(templateModalKind)"
              />
              <Button
                v-if="templateModalKind === 'contract'"
                :label="copy.linkMessageButton"
                icon="i-hugeicons-link-01"
                variant="ghost"
                color="slate"
                size="sm"
                class="w-full"
                data-test="link-message-open"
                @click="openLinkMessage"
              />
              <label class="relative block">
                <span class="sr-only">Buscar modelo</span>
                <span
                  class="i-hugeicons-search-01 pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                />
                <input
                  v-model="templateSearch"
                  type="search"
                  :class="INPUT_CLASS"
                  class="!pl-9"
                  placeholder="Buscar modelo"
                />
              </label>
              <div
                class="-mx-1 grid min-h-0 content-start gap-1.5 overflow-y-auto px-1"
              >
                <div
                  v-for="template in filteredTemplates"
                  :key="template.id"
                  class="group flex items-center gap-2 rounded-xl border p-2.5 transition"
                  :class="
                    templateForm.id === template.id
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-solid-1 hover:border-n-slate-7'
                  "
                >
                  <button
                    type="button"
                    class="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                    :aria-current="templateForm.id === template.id"
                    @click="openTemplateModal(template)"
                  >
                    <span
                      class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                    >
                      <span
                        :class="
                          templateKind(template) === 'quote'
                            ? 'i-hugeicons-file-02'
                            : 'i-hugeicons-legal-document-01'
                        "
                        class="size-4"
                      />
                    </span>
                    <span class="min-w-0 flex-1">
                      <span
                        class="block truncate text-sm font-medium text-n-slate-12"
                        :title="template.name"
                      >
                        {{ template.name }}
                      </span>
                      <span
                        class="mt-0.5 flex items-center gap-1.5 text-xs text-n-slate-10"
                      >
                        <span
                          class="rounded px-1.5 py-px font-medium"
                          :class="
                            template.active === false
                              ? 'bg-n-alpha-2 text-n-slate-11'
                              : 'bg-n-teal-3 text-n-teal-11'
                          "
                        >
                          {{ template.active === false ? 'Inativo' : 'Ativo' }}
                        </span>
                        <span class="truncate">
                          {{
                            templateKind(template) === 'quote'
                              ? 'Orçamento'
                              : `${template.contracts_count || 0} contratos`
                          }}
                        </span>
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="flex size-8 shrink-0 items-center justify-center rounded-lg text-n-slate-10 transition hover:bg-n-ruby-3 hover:text-n-ruby-11"
                    :title="`${copy.delete} ${template.name}`"
                    :aria-label="`${copy.delete} ${template.name}`"
                    @click="removeTemplate(template)"
                  >
                    <span class="i-hugeicons-delete-02 size-4" />
                  </button>
                </div>
                <div
                  v-if="!filteredTemplates.length"
                  class="rounded-xl border border-dashed border-n-weak p-4 text-center text-xs text-n-slate-10"
                >
                  {{
                    templateSearch
                      ? 'Nenhum modelo encontrado para esta busca.'
                      : 'Nenhum modelo criado. Crie o primeiro pelo botão acima.'
                  }}
                </div>
              </div>
            </aside>

            <!-- Template form -->
            <div class="flex min-h-0 flex-col">
              <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
                <div class="flex items-center gap-2">
                  <span class="i-hugeicons-file-edit size-4 text-n-slate-10" />
                  <h3
                    class="m-0 truncate text-sm font-semibold text-n-slate-12"
                  >
                    {{
                      templateForm.id
                        ? `Editando: ${templateForm.name || 'modelo'}`
                        : templateModalKind === 'quote'
                          ? 'Novo modelo de orçamento'
                          : 'Novo modelo de contrato'
                    }}
                  </h3>
                </div>

                <section
                  class="space-y-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                  aria-label="Identificação"
                >
                  <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                    Identificação
                  </h4>
                  <label
                    class="grid gap-1.5 text-sm font-medium text-n-slate-12"
                  >
                    Nome do modelo
                    <input
                      v-model="templateForm.name"
                      :class="INPUT_CLASS"
                      :placeholder="
                        templateModalKind === 'quote'
                          ? 'Ex: Orçamento padrão de locação'
                          : 'Ex: Contrato de adesão'
                      "
                    />
                  </label>
                  <div class="grid gap-2 sm:grid-cols-2">
                    <div
                      class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
                    >
                      <span class="min-w-0">
                        <span class="block text-sm font-medium text-n-slate-12">
                          Modelo ativo
                        </span>
                        <span class="block text-xs text-n-slate-10">
                          Disponível ao gerar documentos.
                        </span>
                      </span>
                      <Switch v-model="templateForm.active" />
                    </div>
                    <div
                      v-if="templateModalKind === 'contract'"
                      class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
                    >
                      <span class="min-w-0">
                        <span class="block text-sm font-medium text-n-slate-12">
                          Exigir selfie ao assinar
                        </span>
                        <span class="block text-xs text-n-slate-10">
                          O cliente autoriza a câmera na assinatura.
                        </span>
                      </span>
                      <Switch v-model="templateForm.require_selfie" />
                    </div>
                  </div>
                </section>

                <section
                  class="space-y-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                  aria-label="Conteúdo"
                >
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <div class="min-w-0">
                      <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                        Conteúdo
                      </h4>
                      <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                        {{ copy.bodyHint }} Os parâmetros são substituídos pelos
                        dados do contrato ao gerar o documento.
                      </p>
                    </div>
                    <Button
                      :label="copy.previewTemplate"
                      icon="i-hugeicons-view"
                      size="sm"
                      faded
                      slate
                      :disabled="!templateForm.body"
                      data-test="template-preview"
                      @click="openTemplatePreview"
                    />
                  </div>
                  <div class="grid gap-2.5">
                    <div
                      v-for="group in PARAMETER_GROUPS.filter(
                        item => !item.quoteOnly || templateModalKind === 'quote'
                      )"
                      :key="group.key"
                      class="grid gap-1.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-start"
                    >
                      <span
                        class="flex items-center gap-1.5 pt-1 text-xs font-medium text-n-slate-11"
                      >
                        <span :class="group.icon" class="size-3.5" />
                        {{ group.label }}
                      </span>
                      <div class="flex flex-wrap gap-1.5">
                        <button
                          v-for="parameter in group.items"
                          :key="parameter"
                          type="button"
                          class="rounded-md border border-n-weak bg-n-solid-1 px-2 py-0.5 font-mono text-xs text-n-brand transition hover:border-n-brand hover:bg-n-brand/5"
                          :title="`Inserir ${parameter} no editor`"
                          @mousedown.prevent="insertParameter(parameter)"
                        >
                          {{ parameter }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="text-xs font-medium text-n-slate-11">
                      {{ copy.bodyFormatLabel }}
                    </span>
                    <div
                      class="inline-flex rounded-lg border border-n-weak bg-n-alpha-1 p-0.5"
                      role="radiogroup"
                      :aria-label="copy.bodyFormatLabel"
                    >
                      <button
                        v-for="option in [
                          { value: 'markdown', label: copy.bodyFormatText },
                          { value: 'html', label: copy.bodyFormatHtml },
                        ]"
                        :key="option.value"
                        type="button"
                        role="radio"
                        :aria-checked="templateBodyFormat === option.value"
                        class="rounded-md px-2.5 py-1 text-xs font-medium transition"
                        :class="
                          templateBodyFormat === option.value
                            ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                            : 'text-n-slate-11 hover:text-n-slate-12'
                        "
                        :data-test="`template-format-${option.value}`"
                        @click="templateBodyFormat = option.value"
                      >
                        {{ option.label }}
                      </button>
                    </div>
                  </div>
                  <p
                    v-if="templateHasHtmlAsCode"
                    class="mb-0 flex flex-wrap items-center gap-2 rounded-lg bg-n-amber-3 px-3 py-2 text-xs text-n-amber-11"
                    role="status"
                  >
                    <span class="i-hugeicons-alert-02 size-4 shrink-0" />
                    <span class="min-w-0 flex-1">{{
                      copy.htmlAsCodeWarning
                    }}</span>
                    <Button
                      :label="copy.convertToHtml"
                      size="sm"
                      color="amber"
                      variant="faded"
                      data-test="template-convert-html"
                      @click="convertTemplateBodyToHtml"
                    />
                  </p>
                  <div
                    v-if="templateBodyFormat === 'html'"
                    class="grid gap-1.5"
                    data-contract-html-editor
                  >
                    <TextArea
                      v-model="templateForm.body"
                      :max-length="5000000"
                      :placeholder="
                        templateModalKind === 'quote'
                          ? '<html>… modelo de orçamento …</html>'
                          : '<html>… modelo de contrato …</html>'
                      "
                      custom-text-area-class="!font-mono !text-xs !leading-relaxed"
                      min-height="20rem"
                      max-height="40rem"
                      resize
                      data-test="template-html-body"
                    />
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ copy.bodyFormatHtmlHint }}
                    </p>
                  </div>
                  <div
                    v-else
                    data-contract-editor
                    class="overflow-hidden rounded-lg border border-n-weak bg-n-solid-1"
                  >
                    <Editor
                      v-model="templateForm.body"
                      editor-key="talkvia-contract-template-editor"
                      :placeholder="
                        templateModalKind === 'quote'
                          ? 'Escreva o modelo de orçamento'
                          : 'Escreva o texto do contrato'
                      "
                      class="[&_.ProseMirror-woot-style]:!min-h-[20rem] [&_.ProseMirror-woot-style]:!max-h-[40rem]"
                      :show-character-count="false"
                      :max-length="0"
                      override-line-breaks
                      enable-variables
                    />
                  </div>
                </section>

                <section
                  v-if="templateModalKind === 'contract'"
                  class="space-y-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                  :aria-label="copy.requiredDocuments"
                >
                  <div>
                    <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                      {{ copy.requiredDocuments }}
                    </h4>
                    <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                      O cliente envia estes documentos ao assinar.
                    </p>
                  </div>
                  <div class="grid gap-2 sm:grid-cols-3">
                    <label
                      v-for="documentName in DEFAULT_DOCUMENTS"
                      :key="documentName"
                      class="flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition"
                      :class="
                        requiredDocuments.includes(documentName)
                          ? 'border-n-brand bg-n-brand/5 text-n-slate-12'
                          : 'border-n-weak bg-n-solid-1 text-n-slate-11'
                      "
                    >
                      <Checkbox
                        :model-value="requiredDocuments.includes(documentName)"
                        @change="toggleRequiredDocument(documentName)"
                      />
                      {{ documentName }}
                    </label>
                  </div>
                  <div
                    v-if="customRequiredDocuments.length"
                    class="flex flex-wrap gap-2"
                  >
                    <span
                      v-for="documentName in customRequiredDocuments"
                      :key="documentName"
                      class="inline-flex items-center gap-1.5 rounded-full border border-n-brand/40 bg-n-brand/5 py-1 pl-3 pr-1 text-xs font-medium text-n-slate-12"
                    >
                      {{ documentName }}
                      <button
                        type="button"
                        class="grid size-5 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
                        :aria-label="`Remover ${documentName}`"
                        @click="toggleRequiredDocument(documentName)"
                      >
                        <span class="i-hugeicons-cancel-01 size-3" />
                      </button>
                    </span>
                  </div>
                  <div class="flex gap-2">
                    <label class="min-w-0 flex-1">
                      <span class="sr-only">{{ copy.customDocument }}</span>
                      <input
                        v-model="customDocumentName"
                        :class="INPUT_CLASS"
                        :placeholder="`${copy.customDocument} (ex.: Contrato social)`"
                        @keyup.enter="addCustomDocument"
                      />
                    </label>
                    <Button
                      label="Adicionar"
                      icon="i-hugeicons-add-01"
                      variant="faded"
                      color="slate"
                      :disabled="!customDocumentName.trim()"
                      @click="addCustomDocument"
                    />
                  </div>
                </section>
                <DeliveryMessageSection
                  :key="`${templateForm.id || 'new'}-${templateModalKind}`"
                  v-model="deliveryMessage"
                  :kind="templateModalKind"
                />
              </div>

              <footer
                class="flex flex-col-reverse gap-2 border-t border-n-weak px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <p class="mb-0 text-xs text-n-slate-10">
                  {{
                    !templateForm.name || !templateForm.body
                      ? 'Informe o nome e o conteúdo para salvar.'
                      : 'Alterações valem para os próximos documentos gerados.'
                  }}
                </p>
                <div class="flex gap-2 sm:justify-end">
                  <Button
                    v-if="templateForm.id"
                    :label="copy.newTemplate"
                    variant="faded"
                    color="slate"
                    @click="resetTemplateForm()"
                  />
                  <Button
                    :label="
                      templateForm.id ? copy.updateTemplate : copy.saveTemplate
                    "
                    icon="i-hugeicons-checkmark-circle-02"
                    :is-loading="isSavingTemplate"
                    :disabled="
                      isSavingTemplate ||
                      !templateForm.name ||
                      !templateForm.body
                    "
                    @click="saveTemplate"
                  />
                </div>
              </footer>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <Dialog
      ref="templateDeleteDialog"
      type="alert"
      title="Excluir modelo?"
      :description="
        pendingTemplateDelete
          ? `O modelo “${pendingTemplateDelete.name}” deixa de estar disponível para novos documentos. Contratos já gerados não são alterados.`
          : ''
      "
      confirm-button-label="Excluir modelo"
      :is-loading="isDeletingTemplate"
      :disable-confirm-button="isDeletingTemplate"
      @confirm="confirmRemoveTemplate"
    />

    <!-- Dashboard modal -->
    <Teleport to="body">
      <div
        v-if="showDashboard"
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-md"
        @click.self="showDashboard = false"
      >
        <section
          class="w-full overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl transition-all duration-200"
          :class="
            isDashboardExpanded
              ? 'my-2 max-w-[calc(100vw-2rem)]'
              : 'my-8 max-w-5xl'
          "
          role="dialog"
          aria-modal="true"
        >
          <header class="border-b border-n-weak px-6 py-4">
            <div class="mb-3 flex items-start justify-between gap-4">
              <div>
                <h2 class="text-lg font-semibold text-n-slate-12">
                  Dashboard de Contratos
                </h2>
                <p class="mb-0 text-xs text-n-slate-10">
                  <template v-if="isDashboardLoading">Carregando...</template>
                  <template v-else>
                    {{ dashboardMetrics.total }} contratos · valor total
                    {{ formatCurrency(dashboardMetrics.totalValue) }}
                  </template>
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :aria-label="
                    isDashboardExpanded ? 'Reduzir modal' : 'Expandir modal'
                  "
                  :title="
                    isDashboardExpanded ? 'Reduzir modal' : 'Expandir modal'
                  "
                  @click="isDashboardExpanded = !isDashboardExpanded"
                >
                  <span
                    class="size-5"
                    :class="
                      isDashboardExpanded
                        ? 'i-lucide-minimize-2'
                        : 'i-lucide-maximize-2'
                    "
                  />
                </button>
                <button
                  type="button"
                  class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :aria-label="copy.close"
                  @click="showDashboard = false"
                >
                  <span class="i-lucide-x size-5" />
                </button>
              </div>
            </div>

            <!-- Date range filter -->
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-xs font-medium text-n-slate-10">Período:</span>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="h-7 rounded-lg px-3 text-xs font-medium transition"
                  :class="
                    !dashboardDateRange.start && !dashboardDateRange.end
                      ? 'bg-n-brand text-white'
                      : 'border border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="setDashboardPreset('all')"
                >
                  Tudo
                </button>
                <button
                  type="button"
                  class="h-7 rounded-lg border border-n-weak px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click="setDashboardPreset('month')"
                >
                  Este mês
                </button>
                <button
                  type="button"
                  class="h-7 rounded-lg border border-n-weak px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click="setDashboardPreset('30d')"
                >
                  30 dias
                </button>
                <button
                  type="button"
                  class="h-7 rounded-lg border border-n-weak px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click="setDashboardPreset('year')"
                >
                  Este ano
                </button>
              </div>
              <div class="flex items-center gap-1.5">
                <DateField
                  v-model="dashboardDateRange.start"
                  size="sm"
                  class="w-36"
                  :max="dashboardDateRange.end || ''"
                  :clearable="false"
                  placeholder="Data inicial"
                />
                <span class="text-xs text-n-slate-10">até</span>
                <DateField
                  v-model="dashboardDateRange.end"
                  size="sm"
                  class="w-36"
                  :min="dashboardDateRange.start || ''"
                  :clearable="false"
                  placeholder="Data final"
                />
                <button
                  type="button"
                  class="h-7 rounded-lg bg-n-brand px-3 text-xs font-semibold text-white transition hover:brightness-95 disabled:opacity-60"
                  :disabled="isDashboardLoading"
                  @click="applyDashboardDateRange"
                >
                  <span
                    v-if="isDashboardLoading"
                    class="i-lucide-loader-circle size-3.5 animate-spin"
                  />
                  <span v-else>Aplicar</span>
                </button>
              </div>
            </div>
          </header>

          <div
            v-if="isDashboardLoading"
            class="flex items-center justify-center py-16"
          >
            <span
              class="i-lucide-loader-circle size-8 animate-spin text-n-brand"
            />
          </div>

          <div v-else class="grid gap-5 p-6">
            <!-- Status cards -->
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <div
                v-for="s in dashboardMetrics.byStatus"
                :key="s.value"
                class="grid gap-1 rounded-xl border p-4"
                :class="
                  statusBadgeClass(s.value)
                    .replace('text-', 'border-')
                    .split(' ')[0] + ' bg-n-solid-1'
                "
              >
                <span class="text-2xl font-bold text-n-slate-12">
                  {{ s.count }}
                </span>
                <span class="text-xs text-n-slate-10">{{ s.label }}</span>
                <div class="mt-1 h-1.5 w-full rounded-full bg-n-alpha-2">
                  <div
                    class="h-1.5 rounded-full bg-current transition-all"
                    :class="
                      statusBadgeClass(s.value)
                        .split(' ')
                        .find(c => c.startsWith('text-'))
                    "
                    :style="{
                      width: dashboardMetrics.total
                        ? `${Math.round((s.count / dashboardMetrics.total) * 100)}%`
                        : '0%',
                    }"
                  />
                </div>
              </div>
            </div>

            <!-- Delivery & value highlights -->
            <div class="grid gap-3 sm:grid-cols-3">
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <p class="mb-1 text-xs text-n-slate-10">Valor total</p>
                <p class="mb-0 text-xl font-bold text-n-slate-12">
                  {{ formatCurrency(dashboardMetrics.totalValue) }}
                </p>
              </div>
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <p class="mb-1 text-xs text-n-slate-10">Com data de entrega</p>
                <p class="mb-0 text-xl font-bold text-n-slate-12">
                  {{ dashboardMetrics.withDelivery }}
                </p>
              </div>
              <div
                class="rounded-xl border p-4"
                :class="
                  dashboardMetrics.overdue > 0
                    ? 'border-n-ruby-5 bg-n-ruby-3'
                    : 'border-n-weak bg-n-solid-1'
                "
              >
                <p class="mb-1 text-xs text-n-slate-10">Entregas atrasadas</p>
                <p
                  class="mb-0 text-xl font-bold"
                  :class="
                    dashboardMetrics.overdue > 0
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ dashboardMetrics.overdue }}
                </p>
              </div>
            </div>

            <div class="grid gap-5 lg:grid-cols-2">
              <!-- By template -->
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
                  Por modelo
                </h3>
                <div class="grid gap-2">
                  <div
                    v-for="t in dashboardMetrics.byTemplate"
                    :key="t.name"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-11"
                    >
                      {{ t.name }}
                    </span>
                    <div class="flex items-center gap-2">
                      <div class="h-1.5 w-20 rounded-full bg-n-alpha-2">
                        <div
                          class="h-1.5 rounded-full bg-n-brand transition-all"
                          :style="{
                            width: dashboardMetrics.total
                              ? `${Math.round((t.count / dashboardMetrics.total) * 100)}%`
                              : '0%',
                          }"
                        />
                      </div>
                      <span
                        class="w-6 text-right text-xs font-semibold text-n-slate-12"
                      >
                        {{ t.count }}
                      </span>
                    </div>
                  </div>
                  <p
                    v-if="!dashboardMetrics.byTemplate.length"
                    class="text-xs text-n-slate-10"
                  >
                    Nenhum dado disponível.
                  </p>
                </div>
              </div>

              <!-- By payment method -->
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
                  Por forma de pagamento
                </h3>
                <div class="grid gap-2">
                  <div
                    v-for="p in dashboardMetrics.byPayment"
                    :key="p.name"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-11"
                    >
                      {{ p.name }}
                    </span>
                    <div class="flex items-center gap-2">
                      <div class="h-1.5 w-20 rounded-full bg-n-alpha-2">
                        <div
                          class="h-1.5 rounded-full bg-n-teal-9 transition-all"
                          :style="{
                            width: dashboardMetrics.total
                              ? `${Math.round((p.count / dashboardMetrics.total) * 100)}%`
                              : '0%',
                          }"
                        />
                      </div>
                      <span
                        class="w-6 text-right text-xs font-semibold text-n-slate-12"
                      >
                        {{ p.count }}
                      </span>
                    </div>
                  </div>
                  <p
                    v-if="!dashboardMetrics.byPayment.length"
                    class="text-xs text-n-slate-10"
                  >
                    Nenhum dado disponível.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showDocumentsModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
        @click.self="showDocumentsModal = false"
      >
        <section
          class="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
          >
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ copy.documentsTitle }}
              </h2>
              <p class="mt-0.5 text-xs text-n-slate-10">
                {{ selectedDocumentsName }}
              </p>
            </div>
            <button
              type="button"
              class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="showDocumentsModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>
          <div class="p-4">
            <p
              v-if="!selectedDocuments.length"
              class="py-6 text-center text-sm text-n-slate-10"
            >
              {{ copy.noDocuments }}
            </p>
            <ul v-else class="flex flex-col gap-2">
              <li
                v-for="(doc, index) in selectedDocuments"
                :key="index"
                class="flex items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3"
              >
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ doc.label || doc.file_name }}
                  </p>
                  <p class="truncate text-xs text-n-slate-10">
                    {{ doc.file_name }}
                  </p>
                </div>
                <a
                  :href="documentDownloadHref(doc)"
                  :download="doc.file_name"
                  class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-n-brand/90"
                >
                  <span class="i-lucide-download size-3.5" />
                  {{ copy.downloadDocument }}
                </a>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </Teleport>
    <CreateNewContactDialog
      ref="createContactDialogRef"
      @create="createContractContact"
    />
    <Dialog
      ref="linkMessageDialog"
      type="edit"
      width="2xl"
      solid
      :title="copy.linkMessageTitle"
      :description="copy.linkMessageDescription"
      :confirm-button-label="copy.linkMessageSave"
      :is-loading="linkMessage.saving"
      :disable-confirm-button="linkMessage.loading"
      @confirm="saveLinkMessage"
    >
      <div class="grid gap-3" data-link-message>
        <div class="flex flex-wrap items-center gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ copy.linkMessageVariables }}
          </span>
          <button
            v-for="variable in LINK_MESSAGE_VARIABLES"
            :key="variable"
            type="button"
            class="rounded-md border border-n-weak bg-n-solid-1 px-2 py-0.5 font-mono text-xs text-n-brand transition hover:border-n-brand hover:bg-n-brand/5"
            @mousedown.prevent="insertLinkMessageVariable(variable)"
          >
            {{ variable }}
          </button>
          <button
            v-if="
              linkMessage.defaultText &&
              linkMessage.text !== linkMessage.defaultText
            "
            type="button"
            class="ms-auto text-xs font-medium text-n-brand hover:underline"
            @click="linkMessage.text = linkMessage.defaultText"
          >
            {{ copy.linkMessageRestore }}
          </button>
        </div>
        <TextArea
          v-model="linkMessage.text"
          :max-length="1000"
          show-character-count
          :disabled="linkMessage.loading"
          data-test="link-message-text"
        />
        <div class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5">
          <p class="mb-1 text-xs font-medium text-n-slate-11">
            {{ copy.linkMessagePreview }}
          </p>
          <p
            class="mb-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
          >
            {{ linkMessagePreview }}
          </p>
        </div>
      </div>
    </Dialog>
    <Dialog
      ref="templatePreviewDialog"
      type="edit"
      width="4xl"
      solid
      :title="copy.previewTitle"
      :description="copy.previewDescription"
      :show-confirm-button="false"
      :cancel-button-label="copy.close || 'Fechar'"
    >
      <div class="grid gap-3" data-test="template-preview-dialog">
        <p
          v-for="warning in templatePreview.warnings"
          :key="warning"
          class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-3 px-3 py-2 text-xs text-n-amber-11"
          role="status"
        >
          <span class="i-hugeicons-alert-02 mt-0.5 size-4 shrink-0" />
          {{ copy.previewWarnings[warning] || warning }}
        </p>
        <p
          v-if="templatePreview.error"
          class="mb-0 rounded-lg bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
          role="alert"
        >
          {{ templatePreview.error }}
        </p>
        <p
          v-else-if="templatePreview.loading"
          class="mb-0 flex items-center gap-2 py-8 text-sm text-n-slate-11"
          role="status"
        >
          <span class="i-hugeicons-loading-03 size-4 animate-spin" />
          {{ copy.previewLoading }}
        </p>
        <iframe
          v-else
          :srcdoc="templatePreview.html"
          sandbox=""
          :title="copy.previewTitle"
          class="h-[65vh] w-full rounded-lg border border-n-weak bg-white"
          data-test="template-preview-frame"
        />
      </div>
    </Dialog>
  </main>
</template>
